import Phaser from 'phaser';
import { BASE_INTEGRITY_MULTIPLIER, COLORS, GAME_HEIGHT, GAME_WIDTH, MAP_FRAME, MAX_COMMAND_RANK, UI_TOP_H, WEAPON_DAMAGE_MULTIPLIER } from '../config/constants';
import type { DamageType } from '../config/constants';
import { audio } from '../core/AudioSynth';
import { music } from '../core/MusicSystem';
import { exitGame } from '../core/ExitGame';
import { formatClearStars, getCommandRank, getRankPerkBonuses, COMMAND_RANKS, saveManager, type LeaderboardEntry } from '../core/SaveManager';
import { leaderboardService, type LeaderboardSnapshot } from '../core/LeaderboardService';
import { mapService } from '../core/MapService';
import { researchManager } from '../core/ResearchManager';
import { RESEARCH_CATEGORIES, RESEARCH, TOTAL_RESEARCH_DATA } from '../data/research';
import { ENEMIES } from '../data/enemies';
import { TOWERS, TOWER_ORDER } from '../data/towers';
import { SYMBIOSIS, SYMBIOSIS_ORDER } from '../data/symbiosis';
import { MISSIONS, getMission, getMissionMap, getMissionWaves, getNextUnbeatenMissionId, isMissionUnlocked } from '../data/missions';
import { deleteUserMap, getMapLayout, getMapRoutes, listUserMaps, userMapToMapDef, type MapDef } from '../data/map';

import { ACHIEVEMENTS } from '../core/AchievementManager';
import { addAudioToggleButtons, addFullscreenIconButton, addRetroButtonHover, addSettingsIconButton, drawScanlines } from '../ui/Retro';
import { FieldManualPanel } from '../ui/FieldManualPanel';
import { ResearchTree, CATEGORY_ART } from '../ui/ResearchTree';
import { applyAudioSettings } from '../core/Settings';
import { rememberScene } from '../core/SessionMemory';
import { voice } from '../core/Voice';
import { navigateTo } from '../core/SceneTransition';
import { bus } from '../core/EventBus';
import { announceAchievement } from '../core/AchievementAudio';
import { applyUiTextScale } from '../ui/UiText';
import { researchVoiceId } from '../data/voiceCallouts';

type Tab = 'mission' | 'research' | 'database' | 'awards' | 'leaderboards';

const PREMIUM_ENEMY_ART: Record<string, string> = {
  scuttler: 'enemy-scuttler-v2',
  husk: 'enemy-husk-v2',
  dashling: 'enemy-dashling-v2',
  plateback: 'enemy-plateback-v2',
  mender: 'enemy-mender-v2',
  broodnode: 'enemy-broodnode-v2',
  veilguard: 'enemy-veilguard-v2',
  sporethrower: 'enemy-sporethrower-v2',
  archon: 'enemy-archon-v2',
  colossus: 'enemy-colossus-v2',
  wasp: 'enemy-wasp-v2',
  hivecrawler: 'enemy-hivecrawler-v2',
  dronebot: 'enemy-dronebot-v2',
  sentrybot: 'enemy-sentrybot-v2',
  haulerbot: 'enemy-haulerbot-v2',
  raider: 'enemy-raider-v2',
  sharpshooter: 'enemy-sharpshooter-v2',
  mechpilot: 'enemy-mechpilot-v2',
  hivequeen: 'enemy-hivequeen-v2',
  reaperbot: 'enemy-reaperbot-v2',
  gunner: 'enemy-gunner-v2',
  goliath: 'enemy-goliath-v2',
  nulltick: 'enemy-nulltick-v2',
  venomspitter: 'enemy-venomspitter-v2',
  locust: 'enemy-locust-v2',
  siegebreaker: 'enemy-siegebreaker-v3',
  warlord: 'enemy-warlord-v2',
  matriarch: 'enemy-matriarch-v2',
  leviathan: 'enemy-leviathan-v2',
};

const PREMIUM_FAMILY_ART: Record<string, string> = {
  insect: 'enemy-locust-v2',
  robot: 'enemy-reaperbot-v2',
  human: 'enemy-gunner-v2',
  mesh: 'enemy-goliath-v2',
};

/** Category medals for the Awards gallery — one texture family per award type. */
const AWARD_MEDAL_BY_ID: Record<string, string> = {
  first_blood: 'award-medal-firstblood-v1',
  hold_kappa: 'award-medal-mission-v1',
  mission_3: 'award-medal-mission-v1',
  mission_6: 'award-medal-mission-v1',
  mission_11: 'award-medal-mission-v1',
  perfect_kappa: 'award-medal-perfect-v1',
  perfect_5: 'award-medal-perfect-v1',
  kill_100: 'award-medal-kills-v1',
  kill_500: 'award-medal-kills-v1',
  kill_1000: 'award-medal-kills-v1',
  kill_2500: 'award-medal-kills-v1',
  art_50: 'award-medal-explosive-v1',
  art_250: 'award-medal-explosive-v1',
  therm_50: 'award-medal-thermal-v1',
  therm_250: 'award-medal-thermal-v1',
  energy_250: 'award-medal-energy-v1',
  ballistic_250: 'award-medal-ballistic-v1',
  wave_10: 'award-medal-waves-v1',
  wave_20: 'award-medal-waves-v1',
  wave_30: 'award-medal-waves-v1',
  centurion_survivor: 'award-medal-waves-v1',
  boss_slayer: 'award-medal-boss-v1',
  boss_5: 'award-medal-boss-v1',
  boss_15: 'award-medal-boss-v1',
  ability_10: 'award-medal-ability-v1',
  ability_50: 'award-medal-ability-v1',
  ability_100: 'award-medal-ability-v1',
  nightmare: 'award-medal-boss-v1',
  rank_5: 'award-medal-rank-v1',
  rank_10: 'award-medal-rank-v1',
  rank_20: 'award-medal-rank-v1',
  research_5: 'award-medal-rank-v1',
  research_15: 'award-medal-rank-v1',
};

function awardMedalKey(id: string): string {
  return AWARD_MEDAL_BY_ID[id] ?? 'award-medal-mission-v1';
}

const WAVE_ENEMY_ART: Record<string, string> = {
  archon: 'enemy-archon-v2',
  dashling: 'enemy-dashling-v2',
  goliath: 'enemy-goliath-v2',
  gunner: 'enemy-gunner-v2',
  hivequeen: 'enemy-hivequeen-v2',
  husk: 'enemy-husk-v2',
  locust: 'enemy-locust-v2',
  nulltick: 'enemy-nulltick-v2',
  plateback: 'enemy-plateback-v2',
  reaperbot: 'enemy-reaperbot-v2',
  scuttler: 'enemy-scuttler-v2',
  venomspitter: 'enemy-venomspitter-v2',
  siegebreaker: 'enemy-siegebreaker-v2',
};

const RANK_PERKS: Record<string, { name: string; desc: string; effect: string }> = {
  CORPORAL: { name: 'FIELD KIT', desc: '+5 starting SUPPLY', effect: '+5 SUPPLY' },
  SERGEANT: { name: 'KINETIC CALIBRATION', desc: '+3% tower damage', effect: '×1.03 tower DMG' },
  LIEUTENANT: { name: 'REINFORCED CORE', desc: '+1 Bastion integrity', effect: '+1 INTEGRITY' },
  CAPTAIN: { name: 'COOLDOWN RELAY', desc: '-4% ability cooldowns', effect: '×0.96 ability CD' },
  MAJOR: { name: 'SUPPLY CACHE', desc: '+8% wave-call rewards', effect: '×1.08 wave reward' },
  COLONEL: { name: 'HARDPOINT AUTHORITY', desc: '+1 base turret hardpoint', effect: '+1 roof hardpoint' },
  BRIGADIER: { name: 'SYMBIOSIS STIPEND', desc: '+5% swap credit', effect: '+5% swap credit' },
  'MAJOR GENERAL': { name: 'DRONE COMMAND', desc: '+1 drone charge', effect: '+1 drone wing' },
  GENERAL: { name: 'APOGEE PROTOCOL', desc: '+10% tower range', effect: '×1.10 tower range' },
};

const RANK_PERK_CODES: Record<string, string> = {
  CORPORAL: 'FIELD',
  SERGEANT: 'KINETIC',
  LIEUTENANT: 'CORE',
  CAPTAIN: 'COOLDOWN',
  MAJOR: 'CACHE',
  COLONEL: 'HARDPOINT',
  BRIGADIER: 'SWAP',
  'MAJOR GENERAL': 'DRONE',
  GENERAL: 'RANGE',
};

export class CommandScene extends Phaser.Scene {
  private tab: Tab = 'mission';
  private difficulty: 'normal' | 'hard' | 'nightmare' = 'normal';
  private dbFilter: 'towers' | 'superpowers' | 'bestiary' = 'towers';
  private dbIndex = 0;
  private dbScroll = 0;
  private researchCategory = 'BALLISTICS';
  private researchScrollX = 0;
  private flash = '';
  private awardsPage = 0;
  private awardsScrollY = 0;
  private lastTab: Tab = 'mission';
  private newAwardIds = new Set<string>();
  private selectedMission = 'kappa';
  private fieldManual?: FieldManualPanel;
  private tree?: ResearchTree;
  private txtData?: Phaser.GameObjects.Text;
  /** Keep the upgrade callout to the first research allocation in this round. */
  private researchVoicePlayedThisRound = false;
  private resetResearchArmed = false;
  private resetResearchTimer?: Phaser.Time.TimerEvent;
  private waveTooltip?: Phaser.GameObjects.Container;
  private loadoutTooltip?: Phaser.GameObjects.Container;
  private leaderboardSnapshot?: LeaderboardSnapshot;
  private leaderboardSnapshotMap = '';
  private leaderboardRequest = 0;
  private previewShotForce = false;
  private previewShotKeyByMap = new Map<string, string>();
  private previewShotSignatureByMap = new Map<string, string>();
  private userMapsOverlay?: HTMLDivElement;
  private leaderboardSelect?: HTMLSelectElement;
  private leaderboardSelectResize?: () => void;
  private leaderboardSelectChanging = false;

  constructor() {
    super('Command');
  }

  init(data?: { tab?: Tab; missionId?: string }): void {
    if (data?.tab) this.tab = data.tab;
    if (data?.missionId && MISSIONS.some((mission) => mission.id === data.missionId)) this.selectedMission = data.missionId;
  }

  private hasActionableResearch(): boolean {
    return RESEARCH.some((node) => researchManager.canUnlock(node));
  }

  private hasNewAward(): boolean {
    const seen = new Set(saveManager.data.awardsSeen ?? []);
    return saveManager.data.achievements.some((id) => !seen.has(id));
  }

  private markAwardsSeen(): void {
    const unlocked = saveManager.data.achievements;
    const seen = saveManager.data.awardsSeen ?? [];
    if (unlocked.length === seen.length && unlocked.every((id) => seen.includes(id))) return;
    saveManager.data.awardsSeen = [...unlocked];
    saveManager.save();
  }

  /** Snapshot of unlocked-but-unseen awards for NEW badges; not cleared on open. */
  private collectNewAwardIds(): Set<string> {
    const seen = new Set(saveManager.data.awardsSeen ?? []);
    return new Set(saveManager.data.achievements.filter((id) => !seen.has(id)));
  }

  private pulseReminder(button: Phaser.GameObjects.Rectangle, colour: number): void {
    const halo = this.add
      .rectangle(button.x, button.y, button.width + 8, button.height + 8, colour, 0.08)
      .setDepth(button.depth - 1);
    this.tweens.add({
      targets: halo,
      alpha: { from: 0.04, to: 0.22 },
      yoyo: true,
      repeat: -1,
      duration: 1800,
      ease: 'Sine.easeInOut',
    });
  }

  create(): void {
    applyAudioSettings();
    audio.unlock();
    music.unlock();
    music.setMenuMode(true);
    rememberScene('Command');
    this.researchVoicePlayedThisRound = false;
    void leaderboardService.syncLocalScores();
    this.difficulty = saveManager.data.difficulty ?? 'normal';
    const offAchievement = bus.on<{ id: string; name: string; desc: string }>('achievement', (def) => {
      if (!def) return;
      this.flash = `COMMENDATION — ${def.name}`;
      announceAchievement(this, def);
      this.time.delayedCall(0, () => this.render());
    });
    // A save invalidates the previous captured mission shot. The next Command
    // render captures the persisted layout, rather than leaving an old image
    // underneath a newly drawn route overlay.
    const onLayoutSaved = (event: Event) => {
      const mapId = (event as CustomEvent<{ mapId?: string }>).detail?.mapId;
      if (!mapId || this.tab !== 'mission') return;
      this.previewShotForce = true;
      if (this.tab === 'mission') this.render(false);
    };
    window.addEventListener('apogee-map-layout-saved', onLayoutSaved);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      window.removeEventListener('apogee-map-layout-saved', onLayoutSaved);
      this.closeUserMapsModal();
    });
    this.render();
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _objects: unknown, _dx: number, dy: number) => {
      // Keep manual scrolling isolated from the database's wheel-scroller.
      if (this.fieldManual?.isWheelTarget(pointer)) return;
      if (this.tab === 'research') {
        if (pointer.x < 240 || pointer.x > 956 || pointer.y < 126 || pointer.y > 566) return;
        const maxScroll = this.researchScrollMax();
        if (maxScroll <= 0) return;
        const delta = Math.abs(_dx) > Math.abs(dy) ? _dx : dy;
        const next = Phaser.Math.Clamp(this.researchScrollX + delta * 0.7, 0, maxScroll);
        if (Math.abs(next - this.researchScrollX) < 0.5) return;
        this.researchScrollX = next;
        this.render(false);
        return;
      }
      if (this.tab === 'awards') {
        // Scroll the awards grid only — keep the commendation header pinned.
        if (pointer.x < 32 || pointer.x > 1248 || pointer.y < 340 || pointer.y > 640) return;
        const maxScroll = this.awardsScrollMax();
        if (maxScroll <= 0) return;
        const next = this.snapAwardsScroll(this.awardsScrollY + dy * 0.9);
        if (Math.abs(next - this.awardsScrollY) < 0.5) return;
        this.awardsScrollY = next;
        this.render(false);
        return;
      }
      if (this.tab !== 'database') return;
      if (pointer.y < 136 || pointer.y > 670) return;
      const maxScroll = this.databaseScrollMax();
      const next = Phaser.Math.Clamp(this.dbScroll + dy * 0.7, 0, maxScroll);
      if (Math.abs(next - this.dbScroll) < 0.5) return;
      this.dbScroll = next;
      this.render(false);
    });
    this.input.keyboard?.on('keydown-ESC', () => {
      this.scene.pause();
      this.scene.launch('Pause', { from: 'Command' });
    });
    this.input.keyboard?.on('keydown-ENTER', () => {
      // A focused leaderboard select owns Enter; never treat map browsing as
      // a deploy command even if the browser also reports the key globally.
      if (this.leaderboardSelectChanging || (typeof document !== 'undefined' && document.activeElement?.getAttribute('aria-label') === 'Leaderboard map')) return;
      this.deploy();
    });
    this.input.keyboard?.on('keydown-H', () => this.fieldManual?.toggle());
    this.input.keyboard?.on('keydown-D', () => {
      if (this.tab === 'mission' && saveManager.data.settings.devMode) navigateTo(this, 'MapForge', { missionId: this.selectedMission });
    });
    applyAudioSettings();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.fieldManual?.destroy();
      this.removeLeaderboardSelect();
      offAchievement();
    });
  }

  update(time: number): void {
    this.tree?.update(time);
  }

  private render(animate = true): void {
    // Mark awards seen only after the player leaves the Awards tab so NEW
    // badges stay visible while they browse the grid.
    if (this.lastTab === 'awards' && this.tab !== 'awards') this.markAwardsSeen();
    this.lastTab = this.tab;
    this.newAwardIds = this.tab === 'awards' ? this.collectNewAwardIds() : this.newAwardIds;
    this.removeLeaderboardSelect();
    this.resetResearchTimer?.remove(false);
    this.resetResearchTimer = undefined;
    this.resetResearchArmed = false;
    this.fieldManual?.destroy();
    this.fieldManual = undefined;
    this.waveTooltip = undefined;
    this.loadoutTooltip = undefined;
    // Detach and destroy the previous render tree. Repeated map-shot refreshes
    // otherwise leave old preview images alive in the renderer, so stale shots
    // can remain visible even after the display list is rebuilt.
    const previousChildren = [...this.children.list];
    this.tree?.destroy();
    this.children.removeAll();
    previousChildren.forEach((child) => { if (child.active) child.destroy(); });
    this.tree = undefined;
    this.txtData = undefined;
    const g = this.add.graphics();
    g.fillStyle(COLORS.bg, 1);
    g.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    if (this.textures.exists('ui-command-grid')) {
      this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'ui-command-grid')
        .setDisplaySize(GAME_WIDTH, GAME_HEIGHT)
        .setAlpha(0.16)
        .setBlendMode(Phaser.BlendModes.SCREEN);
    }
    g.lineStyle(1, 0x121a20, 0.7);
    for (let x = 0; x < GAME_WIDTH; x += 48) g.lineBetween(x, 0, x, GAME_HEIGHT);
    for (let y = 0; y < GAME_HEIGHT; y += 48) g.lineBetween(0, y, GAME_WIDTH, y);
    // Retro CRT scanlines + vector corner brackets
    drawScanlines(g, GAME_WIDTH, GAME_HEIGHT, 0.045, 4);
    const B = 20;
    g.lineStyle(2, COLORS.accent, 0.45);
    g.lineBetween(0, 0, B, 0); g.lineBetween(0, 0, 0, B);
    g.lineBetween(GAME_WIDTH, 0, GAME_WIDTH - B, 0); g.lineBetween(GAME_WIDTH, 0, GAME_WIDTH, B);
    g.lineBetween(0, GAME_HEIGHT, B, GAME_HEIGHT); g.lineBetween(0, GAME_HEIGHT, 0, GAME_HEIGHT - B);
    g.lineBetween(GAME_WIDTH, GAME_HEIGHT, GAME_WIDTH - B, GAME_HEIGHT); g.lineBetween(GAME_WIDTH, GAME_HEIGHT, GAME_WIDTH, GAME_HEIGHT - B);

    // Header
    g.fillStyle(COLORS.panel, 1);
    g.fillRect(0, 0, GAME_WIDTH, 64);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.lineBetween(0, 64, GAME_WIDTH, 64);

    this.add
      .text(24, 10, 'ARC BASTION-7  ·  COMMAND', {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: '18px',
        color: '#c8d4e0',
      });
    const rank = saveManager.data.rank;
    const xp = saveManager.data.xp;
    const rankBand = getCommandRank(rank);
    const atMaxRank = rank >= MAX_COMMAND_RANK;
    const need = atMaxRank ? saveManager.rankThreshold(MAX_COMMAND_RANK) : saveManager.rankThreshold(rank + 1);
    const prev = saveManager.rankThreshold(rank);
    const pct = atMaxRank ? 1 : Phaser.Math.Clamp((xp - prev) / Math.max(1, need - prev), 0, 1);
    this.add.text(24, 36, `${rankBand.title}  ·  RANK ${rank}`, {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#5ec8ff',
    });
    this.add.text(190, 27, atMaxRank ? `XP ${xp}/${need}  ·  MAX` : `XP ${xp}/${need}`, {
      fontFamily: 'Consolas, monospace',
      fontSize: '9px',
      color: '#d4a24a',
    });
    g.fillStyle(0x1a242c, 1);
    g.fillRect(190, 40, 220, 8);
    g.fillStyle(COLORS.accent, 0.9);
    g.fillRect(190, 40, 220 * pct, 8);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.strokeRect(190, 40, 220, 8);
    this.add.text(430, 36, `DATA  ${saveManager.data.researchPoints}`, {
      fontFamily: 'Consolas, monospace',
      fontSize: '12px',
      color: '#d4a24a',
    });

    // Tabs
    const tabs: { id: Tab; label: string }[] = [
      { id: 'mission', label: 'MISSION' },
      { id: 'research', label: 'RESEARCH' },
      { id: 'database', label: 'DATABASE' },
      { id: 'awards', label: 'AWARDS' },
      { id: 'leaderboards', label: 'SCORES' },
    ];
    const tabW = 84;
    const tabGap = 4;
    const navY = 44;
    tabs.forEach((t, i) => {
      const x = 456 + i * (tabW + tabGap);
      const active = this.tab === t.id;
      const reminder = (t.id === 'research' && this.hasActionableResearch())
        || (t.id === 'awards' && this.hasNewAward());
      const bg = this.add
        .rectangle(x, navY, tabW, 28, active ? 0x1a2830 : 0x152028, 1)
        .setStrokeStyle(1, reminder ? (t.id === 'awards' ? 0xd4a24a : COLORS.accent) : active ? COLORS.accent : 0x2a3e4a)
        .setInteractive({ useHandCursor: true });
      if (reminder) this.pulseReminder(bg, t.id === 'awards' ? 0xd4a24a : COLORS.accent);
      addRetroButtonHover(bg, active ? 0x1a2830 : 0x152028);
      bg.on('pointerdown', () => {
        this.tab = t.id;
        if (t.id === 'awards') {
          this.newAwardIds = this.collectNewAwardIds();
          this.focusFirstNewAward();
        }
        audio.click();
        this.render();
      });
      this.add
        .text(x, navY, t.label, {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: active ? '#5ec8ff' : '#6a7a88',
        })
        .setOrigin(0.5);
    });

    // Single exit on Awards; audio/settings live in Pause (ESC) for this tab.
    const awardsMeta = this.tab === 'awards';
    const back = this.add
      .rectangle(GAME_WIDTH - 80, navY, 100, 28, 0x152028, 1)
      .setStrokeStyle(1, 0x2a3e4a)
      .setInteractive({ useHandCursor: true });
    addRetroButtonHover(back);
    back.on('pointerdown', () => {
      navigateTo(this, 'Menu');
    });
    this.add
      .text(GAME_WIDTH - 80, navY, 'MENU [ESC]', {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: '#c8d4e0',
      })
      .setOrigin(0.5);

    if (!awardsMeta) {
      const exit = this.add
        .rectangle(GAME_WIDTH - 190, navY, 90, 28, 0x10161c, 1)
        .setStrokeStyle(1, 0x2a3e4a)
        .setInteractive({ useHandCursor: true });
      exit.on('pointerover', () => exit.setFillStyle(0x1a2830));
      exit.on('pointerout', () => exit.setFillStyle(0x10161c));
      exit.on('pointerdown', () => {
        audio.click();
        exitGame();
      });
      this.add
        .text(GAME_WIDTH - 190, navY, 'EXIT GAME', {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: '#6a7a88',
        })
        .setOrigin(0.5);
      addAudioToggleButtons(this, 910, navY);
      addFullscreenIconButton(this, 980, navY);
      addSettingsIconButton(this, 1022, navY, () => {
        this.scene.pause();
        this.scene.launch('Pause', { from: 'Command' });
      });
    } else {
      this.add.text(GAME_WIDTH - 200, navY, 'AUDIO / SETTINGS  ·  ESC PAUSE', {
        fontFamily: 'Consolas, monospace',
        fontSize: '9px',
        color: '#5a6e7c',
      }).setOrigin(0.5);
    }

    if (this.flash) {
      this.add
        .text(GAME_WIDTH / 2, 80, this.flash, {
          fontFamily: 'Consolas, monospace',
          fontSize: '14px',
          color: '#d4a24a',
        })
        .setOrigin(0.5);
    }

    if (this.tab === 'mission') this.renderMission(g);
    else if (this.tab === 'research') this.renderResearch(g);
    else if (this.tab === 'awards') this.renderAwards(g);
    else if (this.tab === 'leaderboards') this.renderLeaderboards(g);
    else this.renderDatabase(g);

    this.fieldManual = new FieldManualPanel(this, 130);
    applyUiTextScale(this);
    if (animate) this.cameras.main.fadeIn(160, 0, 0, 0);
  }

  private renderLeaderboards(g: Phaser.GameObjects.Graphics): void {
    const mapId = this.selectedMission;
    const mission = getMission(mapId);
    const mapName = mission.name;
    const localSnapshot: LeaderboardSnapshot = {
      map: saveManager.getLeaderboard(mapId, 10),
      overall: saveManager.getLeaderboard('overall', 10),
      source: 'local',
    };
    const snapshot = this.leaderboardSnapshotMap === mapId && this.leaderboardSnapshot
      ? this.leaderboardSnapshot
      : localSnapshot;

    if (this.leaderboardSnapshotMap !== mapId) {
      const request = ++this.leaderboardRequest;
      void leaderboardService.read(mapId).then((remoteSnapshot) => {
        if (request !== this.leaderboardRequest || this.tab !== 'leaderboards') return;
        this.leaderboardSnapshot = remoteSnapshot;
        this.leaderboardSnapshotMap = mapId;
        this.render(false);
      });
    }

    const commander = saveManager.data.commanderName || 'ARC-CADET';
    const mapRows = snapshot.map.slice(0, 10);
    const overallRows = snapshot.overall.slice(0, 10);
    const ownMapIndex = mapRows.findIndex((row) => row.commander === commander);
    const ownOverallIndex = overallRows.findIndex((row) => row.commander === commander);
    const ownBest = ownMapIndex >= 0 ? mapRows[ownMapIndex] : undefined;
    const nextMissionId = getNextUnbeatenMissionId(saveManager.data.missionsComplete, saveManager.data.settings.devMode);

    this.add.text(40, 78, 'COMMAND LADDER  ·  FIELD PERFORMANCE', {
      fontFamily: 'Consolas, monospace', fontSize: '14px', color: '#6a7a88',
    });
    this.add.text(280, 101, 'SELECT THEATRE  ·  RECORDS UPDATE LIVE', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#4a5560',
    });
    this.mountLeaderboardSelect();
    this.add.text(GAME_WIDTH - 40, 80, snapshot.source === 'remote' ? 'SHARED NETWORK BOARD' : 'LOCAL DEVICE BOARD', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: snapshot.source === 'remote' ? '#7dce5a' : '#d4a24a',
    }).setOrigin(1, 0);

    const panel = (x: number, y: number, w: number, h: number, accent = COLORS.panelEdge): void => {
      g.fillStyle(0x0e171e, 0.97).fillRoundedRect(x, y, w, h, 8);
      g.lineStyle(1.2, accent, 0.9).strokeRoundedRect(x, y, w, h, 8);
      g.lineStyle(1, 0x274351, 0.65).lineBetween(x + 16, y + 42, x + w - 16, y + 42);
    };

    panel(40, 136, 1200, 108, Number(mission.accent.replace('#', '0x')) || COLORS.accent);
    this.add.text(64, 154, `THEATRE  //  ${mapName}`, {
      fontFamily: 'Consolas, monospace', fontSize: '17px', color: '#e0e8f0', fontStyle: 'bold',
    });
    this.add.text(64, 181, `${mission.index.toString().padStart(2, '0')}  ·  ${mission.unlocksNote.toUpperCase()}`, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#8fa8b8',
    });
    this.add.text(64, 210, ownBest ? `YOUR BEST  ${ownBest.score.toLocaleString()}  ·  ${formatClearStars(ownBest.stars)}  ·  ${ownBest.integrity} INTEGRITY` : 'YOUR BEST  —  ·  CLEAR THIS THEATRE TO SET A RECORD', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: ownBest ? '#d4a24a' : '#6a7a88',
    });

    const stat = (x: number, label: string, value: string, colour: string): void => {
      this.add.text(x, 158, label, { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88' });
      this.add.text(x, 176, value, { fontFamily: 'Consolas, monospace', fontSize: '15px', color: colour, fontStyle: 'bold' });
    };
    stat(505, 'MAP RANK', ownMapIndex >= 0 ? `#${ownMapIndex + 1}` : '—', '#5ec8ff');
    stat(630, 'OVERALL RANK', ownOverallIndex >= 0 ? `#${ownOverallIndex + 1}` : '—', '#d4a24a');
    stat(770, 'LOCAL CLEARS', String(mapRows.length), '#7dce5a');
    stat(900, 'BOARD SOURCE', snapshot.source === 'remote' ? 'LIVE' : 'LOCAL', snapshot.source === 'remote' ? '#7dce5a' : '#d4a24a');

    const nextButton = this.add.rectangle(1120, 198, 206, 30, 0x152028, 1)
      .setStrokeStyle(1, nextMissionId ? COLORS.accent : COLORS.panelEdge)
      .setInteractive({ useHandCursor: !!nextMissionId });
    this.add.text(1120, 198, nextMissionId ? 'NEXT OPERATION  ›' : 'CAMPAIGN CLEAR', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: nextMissionId ? '#5ec8ff' : '#6a7a88',
    }).setOrigin(0.5);
    if (nextMissionId) {
      nextButton.on('pointerdown', () => {
        this.selectedMission = nextMissionId;
        this.tab = 'mission';
        audio.click();
        this.render();
      });
      addRetroButtonHover(nextButton);
    }

    panel(40, 262, 500, 390, COLORS.accent);
    this.add.text(62, 280, `THEATRE PODIUM  ·  ${mapName}`, {
      fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#5ec8ff',
    });
    this.add.text(518, 282, `${mapRows.length ? `${mapRows.length} RECORDS` : 'AWAITING FIRST CLEAR'}`, {
      fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88',
    }).setOrigin(1, 0);
    const podium = mapRows.slice(0, 3);
    if (!podium.length) {
      this.add.text(62, 348, 'NO FIELD RECORDS YET', { fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#6a7a88' });
      this.add.text(62, 374, 'Clear the theatre to enter the command ladder.', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#8fa8b8' });
    } else {
      podium.forEach((row, index) => {
        const rowY = 334 + index * 84;
        const isMine = row.commander === commander;
        g.fillStyle(index === 0 ? 0x2a2920 : isMine ? 0x172832 : 0x101a21, 0.92).fillRoundedRect(58, rowY, 464, 64, 5);
        g.lineStyle(1, index === 0 ? 0xd4a24a : isMine ? COLORS.accent : COLORS.panelEdge, isMine || index === 0 ? 0.9 : 0.7).strokeRoundedRect(58, rowY, 464, 64, 5);
        this.add.text(76, rowY + 12, `0${index + 1}`, { fontFamily: 'Consolas, monospace', fontSize: '18px', color: index === 0 ? '#d4a24a' : '#6a7a88', fontStyle: 'bold' });
        this.add.text(130, rowY + 10, row.commander.slice(0, 18), { fontFamily: 'Consolas, monospace', fontSize: '12px', color: isMine ? '#e0f4ff' : '#c8d4e0' });
        this.add.text(130, rowY + 32, `${formatClearStars(row.stars)}  ·  ${row.integrity} INT  ·  ${row.waves} WAVES`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#8fa8b8' });
        this.add.text(500, rowY + 14, row.score.toLocaleString(), { fontFamily: 'Consolas, monospace', fontSize: '15px', color: '#9ad4e8', fontStyle: 'bold' }).setOrigin(1, 0);
        this.add.text(500, rowY + 38, row.difficulty.toUpperCase(), { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88' }).setOrigin(1, 0);
      });
    }

    panel(560, 262, 680, 390, COLORS.supply);
    this.add.text(582, 280, 'COMMAND EXCHANGE  ·  OVERALL', { fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#d4a24a' });
    this.add.text(1218, 282, 'TOP 10', { fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88' }).setOrigin(1, 0);
    const overallList = overallRows.slice(0, 8);
    if (!overallList.length) {
      this.add.text(582, 348, 'NO SHARED RECORDS YET', { fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#6a7a88' });
      this.add.text(582, 374, 'Your first clear will establish the theatre baseline.', { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#8fa8b8' });
    } else {
      overallList.forEach((row, index) => {
        const rowY = 324 + index * 38;
        const isMine = row.commander === commander;
        if (index % 2 === 0 || isMine) g.fillStyle(isMine ? 0x172832 : 0x152028, 0.7).fillRect(578, rowY - 5, 644, 29);
        this.add.text(590, rowY, String(index + 1).padStart(2, '0'), { fontFamily: 'Consolas, monospace', fontSize: '10px', color: index === 0 ? '#d4a24a' : '#6a7a88' });
        this.add.text(626, rowY, row.commander.slice(0, 16), { fontFamily: 'Consolas, monospace', fontSize: '10px', color: isMine ? '#e0f4ff' : '#c8d4e0' });
        this.add.text(805, rowY, row.mapName.slice(0, 19), { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#8fa8b8' });
        this.add.text(1000, rowY, `${formatClearStars(row.stars)}  ${row.waves}W`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#d4a24a' });
        this.add.text(1218, rowY, row.score.toLocaleString(), { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#9ad4e8', fontStyle: 'bold' }).setOrigin(1, 0);
      });
    }
  }

  /** Native select overlay for instant map scoreboard switching. */
  private mountLeaderboardSelect(): void {
    if (typeof document === 'undefined') return;
    const select = document.createElement('select');
    select.setAttribute('aria-label', 'Leaderboard map');
    select.style.cssText = 'position:fixed;z-index:35;border:1px solid #31566a;border-radius:4px;background:#0e1b24;color:#bcecff;padding:5px 28px 5px 9px;font:11px Consolas,Menlo,monospace;cursor:pointer;';
    MISSIONS.forEach((mission) => {
      const option = document.createElement('option');
      option.value = mission.id;
      option.textContent = mission.name;
      option.selected = mission.id === this.selectedMission;
      select.appendChild(option);
    });
    select.addEventListener('change', () => {
      const next = select.value;
      if (!MISSIONS.some((mission) => mission.id === next) || next === this.selectedMission) return;
      this.selectedMission = next;
      this.leaderboardSnapshot = undefined;
      this.leaderboardSnapshotMap = '';
      audio.click();
      this.leaderboardSelectChanging = true;
      // Defer the Phaser redraw until the browser has finished dispatching the
      // select keyboard event; this keeps the focused control active long
      // enough for the global deploy shortcut guard to run.
      this.time.delayedCall(0, () => {
        this.render();
        this.leaderboardSelectChanging = false;
      });
    });
    // Phaser listens for Enter globally to deploy a mission. Keep keyboard
    // interaction inside the native select when a commander is browsing maps.
    select.addEventListener('keydown', (event) => event.stopImmediatePropagation());
    select.addEventListener('keyup', (event) => event.stopImmediatePropagation());
    document.body.appendChild(select);
    this.leaderboardSelect = select;
    const position = () => {
      const canvas = this.game.canvas;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const sx = rect.width / GAME_WIDTH;
      const sy = rect.height / GAME_HEIGHT;
      select.style.left = `${rect.left + 40 * sx}px`;
      select.style.top = `${rect.top + 103 * sy}px`;
      select.style.width = `${Math.max(180, 230 * sx)}px`;
      select.style.height = `${Math.max(26, 28 * sy)}px`;
      select.style.fontSize = `${Math.max(10, 11 * sx)}px`;
    };
    this.leaderboardSelectResize = position;
    window.addEventListener('resize', position);
    position();
  }

  private removeLeaderboardSelect(): void {
    if (this.leaderboardSelectResize && typeof window !== 'undefined') window.removeEventListener('resize', this.leaderboardSelectResize);
    this.leaderboardSelectResize = undefined;
    this.leaderboardSelect?.remove();
    this.leaderboardSelect = undefined;
  }

  private static readonly AWARDS_COLS = 3;
  private static readonly AWARDS_CARD_H = 58;
  private static readonly AWARDS_GAP_Y = 8;
  private static readonly AWARDS_ROWS_VISIBLE = 5;

  private awardsViewportTop(): number { return 300; }
  private awardsViewportHeight(): number {
    const h = CommandScene.AWARDS_CARD_H;
    const gap = CommandScene.AWARDS_GAP_Y;
    const rows = CommandScene.AWARDS_ROWS_VISIBLE;
    return rows * h + (rows - 1) * gap;
  }
  private awardsRowHeight(): number {
    return CommandScene.AWARDS_CARD_H + CommandScene.AWARDS_GAP_Y;
  }
  private awardsTotalRows(): number {
    return Math.ceil(ACHIEVEMENTS.length / CommandScene.AWARDS_COLS);
  }

  /** Scroll range in whole rows — cards never sit half-out of the viewport. */
  private awardsScrollMax(): number {
    const rowsVisible = CommandScene.AWARDS_ROWS_VISIBLE;
    return Math.max(0, (this.awardsTotalRows() - rowsVisible) * this.awardsRowHeight());
  }

  /** Snap any scroll value onto a row boundary. */
  private snapAwardsScroll(value: number): number {
    const rowH = this.awardsRowHeight();
    const snapped = Math.round(value / rowH) * rowH;
    return Phaser.Math.Clamp(snapped, 0, this.awardsScrollMax());
  }

  /** Scroll the gallery so the first NEW award is visible on open. */
  private focusFirstNewAward(): void {
    const newIds = this.newAwardIds;
    if (!newIds.size) {
      this.awardsScrollY = 0;
      return;
    }
    const firstIndex = ACHIEVEMENTS.findIndex((a) => newIds.has(a.id));
    if (firstIndex < 0) {
      this.awardsScrollY = 0;
      return;
    }
    const cols = CommandScene.AWARDS_COLS;
    const row = Math.floor(firstIndex / cols);
    // Show one row of context above when there is room.
    const targetRow = Phaser.Math.Clamp(row - 1, 0, Math.max(0, this.awardsTotalRows() - CommandScene.AWARDS_ROWS_VISIBLE));
    this.awardsScrollY = this.snapAwardsScroll(targetRow * this.awardsRowHeight());
  }

  private renderAwards(g: Phaser.GameObjects.Graphics): void {
    const rank = saveManager.data.rank;
    const xp = saveManager.data.xp;
    const band = getCommandRank(rank);
    const nextBand = COMMAND_RANKS.find((candidate) => candidate.min > rank) ?? band;
    const currentPerk = RANK_PERKS[band.title] ?? RANK_PERKS.CORPORAL;
    const nextPerk = RANK_PERKS[nextBand.title] ?? currentPerk;
    const currentThreshold = saveManager.rankThreshold(rank);
    const atMaxRank = rank >= MAX_COMMAND_RANK;
    const nextThreshold = atMaxRank ? currentThreshold : saveManager.rankThreshold(rank + 1);
    const progress = atMaxRank ? 1 : Phaser.Math.Clamp((xp - currentThreshold) / Math.max(1, nextThreshold - currentThreshold), 0, 1);
    const live = getRankPerkBonuses(rank);
    const unlocked = new Set(saveManager.data.achievements);
    const newIds = this.newAwardIds;
    const total = ACHIEVEMENTS.length;
    const unlockedCount = ACHIEVEMENTS.filter((a) => unlocked.has(a.id)).length;
    const newCount = ACHIEVEMENTS.filter((a) => unlocked.has(a.id) && newIds.has(a.id)).length;

    const ACCENT = 0x5ec8ff;
    const GOLD = 0xffd27a;
    const GOOD = 0x7dce5a;
    const PANEL = 0x0b1620;

    const label = (
      x: number,
      y: number,
      content: string,
      opts: { size: number; color: string; bold?: boolean; stroke?: string; origin?: [number, number]; align?: 'left' | 'center' | 'right' },
    ) => {
      const t = this.add.text(x, y, content, {
        fontFamily: 'Consolas, Menlo, monospace',
        fontSize: `${opts.size}px`,
        color: opts.color,
        fontStyle: opts.bold ? 'bold' : 'normal',
        stroke: opts.stroke ?? '#050a0e',
        strokeThickness: opts.bold ? 3 : 2,
        align: opts.align ?? 'left',
      }).setResolution(3);
      if (opts.origin) t.setOrigin(opts.origin[0], opts.origin[1]);
      return t;
    };

    /** Section header: short accent bar + caps label — reads as one system. */
    const sectionHeader = (x: number, y: number, text: string, accent = ACCENT) => {
      g.fillStyle(accent, 1);
      g.fillRoundedRect(x, y + 1, 3, 12, 1.5);
      label(x + 10, y, text, { size: 11, color: '#93a9b8' });
    };

    // ================= PRIMARY · COMMAND GRADE =================
    sectionHeader(36, 84, 'PRIMARY  ·  COMMAND GRADE', GOLD);
    g.fillStyle(PANEL, 1);
    g.fillRoundedRect(36, 102, 760, 156, 10);
    g.lineStyle(2, GOLD, 0.9);
    g.strokeRoundedRect(36, 102, 760, 156, 10);
    // Top sheen so the panel reads as a lit surface, not a flat box.
    g.fillStyle(GOLD, 0.06);
    g.fillRoundedRect(38, 104, 756, 30, 8);

    label(56, 116, band.title, { size: 22, color: '#ffffff', bold: true });
    label(56, 146, `RANK ${rank}  ·  ${band.abbreviation}  ·  ${atMaxRank ? 'FINAL GRADE' : `${Math.round(progress * 100)}% TO ${nextBand.title}`}`, {
      size: 12, color: '#b8e4f5',
    });

    // XP rail with ticks + cap so progress is legible at a glance.
    const railX = 56;
    const railY = 172;
    const railW = 380;
    g.fillStyle(0x16232c, 1);
    g.fillRoundedRect(railX, railY, railW, 12, 6);
    if (progress > 0) {
      g.fillStyle(0x9c7a2e, 1);
      g.fillRoundedRect(railX, railY, railW * progress, 12, 6);
      g.fillStyle(GOLD, 1);
      g.fillRoundedRect(railX, railY, railW * progress, 6, 3);
      g.fillStyle(0xfff2c8, 1);
      g.fillCircle(railX + railW * progress, railY + 6, 4);
    }
    g.lineStyle(1, 0x2f4653, 0.9);
    for (let i = 1; i < 10; i++) g.lineBetween(railX + (railW / 10) * i, railY + 1, railX + (railW / 10) * i, railY + 11);
    label(railX + railW + 12, railY - 2, atMaxRank ? 'MAX' : `${xp} / ${nextThreshold} XP`, { size: 11, color: '#ffd27a' });

    label(56, 194, `ACTIVE PERK  ·  ${currentPerk.name}`, { size: 13, color: '#ffe08a', bold: true });
    label(56, 214, currentPerk.desc, { size: 13, color: '#e8f4ff' });
    // Effect chip — the number the player actually cares about.
    const effectText = currentPerk.effect;
    const chipW = Math.max(96, effectText.length * 8 + 20);
    g.fillStyle(0x16301c, 1);
    g.fillRoundedRect(540, 192, chipW, 22, 5);
    g.lineStyle(1.5, GOOD, 0.95);
    g.strokeRoundedRect(540, 192, chipW, 22, 5);
    label(540 + chipW / 2, 197, effectText, { size: 12, color: '#c8ffb0', bold: true, origin: [0.5, 0] });

    // Next unlock strip
    g.fillStyle(0x101c24, 1);
    g.fillRoundedRect(52, 224, 728, 26, 5);
    g.lineStyle(1, 0x2f4653, 1);
    g.strokeRoundedRect(52, 224, 728, 26, 5);
    label(64, 230, atMaxRank
      ? 'NEXT UNLOCK  ·  ALL LADDER PERKS EARNED'
      : `NEXT UNLOCK @ R${nextBand.min}  ·  ${nextPerk.name}  ·  ${nextPerk.effect}`, {
      size: 12, color: '#9ad4e8',
    });

    // Rank emblem — single strong focal element on the right of the panel.
    const emX = 716;
    const emY = 180;
    const emR = 52;
    g.fillStyle(0x0e1c26, 1);
    g.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 3;
      const px = emX + Math.cos(a) * emR;
      const py = emY + Math.sin(a) * emR * 0.9;
      if (i === 0) g.moveTo(px, py); else g.lineTo(px, py);
    }
    g.closePath();
    g.fillPath();
    g.lineStyle(2, GOLD, 0.95);
    g.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = -Math.PI / 2 + (i * Math.PI) / 3;
      const px = emX + Math.cos(a) * emR;
      const py = emY + Math.sin(a) * emR * 0.9;
      if (i === 0) g.moveTo(px, py); else g.lineTo(px, py);
    }
    g.closePath();
    g.strokePath();
    g.fillStyle(GOLD, 0.1);
    g.fillCircle(emX, emY, emR * 0.78);
    label(emX, emY - 22, String(rank), { size: 30, color: '#ffffff', bold: true, origin: [0.5, 0] });
    label(emX, emY + 14, band.abbreviation, { size: 12, color: '#ffd27a', bold: true, origin: [0.5, 0] });

    // ================= SECONDARY · ACTIVE BUFFS =================
    sectionHeader(812, 84, 'SECONDARY  ·  WHAT YOU BRING', ACCENT);
    g.fillStyle(PANEL, 0.96);
    g.fillRoundedRect(812, 102, 428, 156, 10);
    g.lineStyle(1.5, 0x35576a, 1);
    g.strokeRoundedRect(812, 102, 428, 156, 10);

    const buffs: Array<[string, string, boolean]> = [
      ['Starting supply', `+${live.startSupplyBonus}`, live.startSupplyBonus > 0],
      ['Tower damage', `x${live.towerDamageMul.toFixed(2)}`, live.towerDamageMul > 1],
      ['Bastion integrity', `+${live.integrityBonus}`, live.integrityBonus > 0],
      ['Ability cooldown', `x${live.abilityCooldownMul.toFixed(2)}`, live.abilityCooldownMul < 1],
      ['Wave reward', `x${live.waveRewardMul.toFixed(2)}`, live.waveRewardMul > 1],
      ['Tower range', `x${live.towerRangeMul.toFixed(2)}`, live.towerRangeMul > 1],
    ];
    buffs.forEach(([name, value, active], i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const rowX = 826 + col * 202;
      const rowY = 116 + row * 32;
      g.fillStyle(i % 2 === 0 ? 0x10202a : 0x0d1a22, 1);
      g.fillRoundedRect(rowX, rowY, 192, 28, 4);
      // Active buffs get a green pip; inactive stats stay quiet.
      g.fillStyle(active ? GOOD : 0x3a4d58, 1);
      g.fillCircle(rowX + 10, rowY + 14, 3);
      label(rowX + 20, rowY + 8, name, { size: 11, color: '#9ab4c4' });
      label(rowX + 182, rowY + 7, value, { size: 13, color: active ? '#c8ffb0' : '#6f8492', bold: true, origin: [1, 0] });
    });

    g.lineStyle(1, 0x2f4653, 1);
    g.lineBetween(826, 214, 1226, 214);
    label(826, 220, 'RANK TIMELINE', { size: 10, color: '#7a8a96' });
    const upcoming = COMMAND_RANKS.filter((c) => c.min > rank).slice(0, 4);
    const timeline = upcoming.length
      ? upcoming
      : [COMMAND_RANKS[COMMAND_RANKS.length - 1]];
    timeline.forEach((c, i) => {
      const chipX = 826 + i * 100;
      const chipY = 234;
      const reached = rank >= c.min;
      g.fillStyle(reached ? 0x16301c : 0x101c24, 1);
      g.fillRoundedRect(chipX, chipY, 92, 20, 4);
      g.lineStyle(1.2, reached ? GOOD : 0x35576a, 1);
      g.strokeRoundedRect(chipX, chipY, 92, 20, 4);
      label(chipX + 8, chipY + 4, `R${c.min} ${c.abbreviation}`, {
        size: 10, color: reached ? '#c8ffb0' : '#8aa0b0', bold: reached,
      });
    });

    // ================= TERTIARY · AWARDS GALLERY =================
    const viewTop = this.awardsViewportTop();
    const viewH = this.awardsViewportHeight();
    const viewBottom = viewTop + viewH;
    const scrollMax = this.awardsScrollMax();
    if (newCount > 0 && this.awardsScrollY === 0) this.focusFirstNewAward();
    this.awardsScrollY = this.snapAwardsScroll(this.awardsScrollY);

    // ARC unit crest anchors the gallery header — medals carry the cards.
    if (this.textures.exists('award-crest-v1')) {
      this.add.image(48, 278, 'award-crest-v1').setDisplaySize(52, 52).setAlpha(0.95);
      sectionHeader(88, 268, `AWARDS GALLERY  ·  ${unlockedCount}/${total} EARNED`, ACCENT);
    } else {
      sectionHeader(36, 268, `AWARDS GALLERY  ·  ${unlockedCount}/${total} EARNED`, ACCENT);
    }
    if (newCount > 0) {
      const banner = this.add.rectangle(520, 278, 264, 24, GOLD, 1)
        .setStrokeStyle(2, 0xffe8a0)
        .setInteractive({ useHandCursor: true });
      addRetroButtonHover(banner, GOLD);
      banner.on('pointerdown', () => { audio.click(); this.focusFirstNewAward(); this.render(); });
      label(520, 278, `${newCount} NEW  ·  JUMP TO FIRST`, { size: 12, color: '#1a1408', bold: true, origin: [0.5, 0.5] });
    } else {
      const pct = scrollMax > 0 ? Math.round((this.awardsScrollY / scrollMax) * 100) : 100;
      label(520, 278, scrollMax > 0 ? `ROW ${Math.round(this.awardsScrollY / this.awardsRowHeight()) + 1}/${Math.max(1, this.awardsTotalRows() - CommandScene.AWARDS_ROWS_VISIBLE + 1)}  ·  ${pct}%` : 'ALL AWARDS VISIBLE', {
        size: 11, color: '#6a7a88', origin: [0.5, 0.5],
      });
    }

    // Farm route lives in the header band so it never crosses the card grid.
    if (saveManager.data.researchPoints < 5) {
      const farm = this.add.rectangle(1120, 278, 212, 24, 0x1f2410, 1)
        .setStrokeStyle(1.4, GOLD)
        .setInteractive({ useHandCursor: true });
      addRetroButtonHover(farm, 0x1f2410);
      farm.on('pointerdown', () => { audio.click(); this.tab = 'research'; this.flash = 'CLEAR MISSIONS TO FARM DATA  ·  +15 DATA / MAP'; this.render(); });
      label(1120, 278, `DATA ${saveManager.data.researchPoints}  ·  FARM ROUTE`, { size: 11, color: '#ffd27a', bold: true, origin: [0.5, 0.5] });
    }

    // Gallery frame — exact multiple of the row grid, so cards can never bleed.
    g.fillStyle(0x081117, 0.96);
    g.fillRoundedRect(32, viewTop - 8, 1216, viewH + 16, 12);
    g.lineStyle(1.5, 0x2c4c5e, 1);
    g.strokeRoundedRect(32, viewTop - 8, 1216, viewH + 16, 12);

    const cols = CommandScene.AWARDS_COLS;
    const cardW = 392;
    const cardH = CommandScene.AWARDS_CARD_H;
    const gapX = 10;
    const rowH = this.awardsRowHeight();
    const gridX = 40;

    ACHIEVEMENTS.forEach((a, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = gridX + col * (cardW + gapX);
      const y = viewTop + row * rowH - this.awardsScrollY;
      // Row-snapped scroll means every visible card fits its row exactly.
      if (y < viewTop - 0.5 || y + cardH > viewBottom + 0.5) return;

      const has = unlocked.has(a.id);
      const isNew = has && newIds.has(a.id);
      const stars = a.difficultyStars ? (saveManager.data.achievementStars?.[a.id] ?? (has ? 1 : 0)) : 0;
      const skillIncomplete = Boolean(a.difficultyStars) && (!has || stars < 3);
      const border = isNew ? GOLD : has ? GOOD : 0x4a6a7c;
      const fill = isNew ? 0x2a2410 : has ? 0x12281c : 0x0d141a;

      g.fillStyle(fill, 1);
      g.fillRoundedRect(x, y, cardW, cardH, 7);
      // Soft plate behind the medal well.
      g.fillStyle(has || isNew ? 0x081016 : 0x0a1014, 0.9);
      g.fillRoundedRect(x + 8, y + 8, 42, cardH - 16, 6);
      g.fillStyle(border, isNew ? 1 : has ? 0.95 : 0.55);
      g.fillRoundedRect(x, y + 6, 4, cardH - 12, 2);
      g.lineStyle(isNew ? 2.5 : has ? 1.4 : 1.2, border, isNew ? 1 : has ? 0.85 : 0.7);
      g.strokeRoundedRect(x, y, cardW, cardH, 7);
      if (isNew) {
        g.fillStyle(GOLD, 0.1);
        g.fillRoundedRect(x, y, cardW, cardH, 7);
      }

      const medalKey = awardMedalKey(a.id);
      if (this.textures.exists(medalKey)) {
        const medal = this.add.image(x + 29, y + cardH / 2, medalKey);
        medal.setDisplaySize(38, 38);
        medal.setAlpha(has || isNew ? 1 : 0.28);
        if (!has && !isNew) medal.setTint(0x6a7a88);
      }

      const textX = x + 58;
      const titleLimit = isNew ? cardW - 74 : cardW - 20;
      label(textX, y + 8, a.name, {
        size: 14,
        color: isNew ? '#fff6d0' : has ? '#d4ff9a' : '#c2d2de',
        bold: true,
      });
      void titleLimit;
      label(textX, y + 29, a.desc, { size: 12, color: has ? '#c8e8ff' : '#8aa0b0' });

      if (skillIncomplete) {
        const starLabel = formatClearStars(Math.min(3, stars || 0));
        label(textX, y + cardH - 15, has
          ? `${starLabel}  SKILL ${Math.min(3, stars)}/3  ·  RAISE DIFFICULTY`
          : `${starLabel}  CLEAR ON HARD / NIGHTMARE`, {
          size: 11, color: has ? '#ffd27a' : '#95a9b8',
        });
      } else if (has) {
        label(textX, y + cardH - 15, a.difficultyStars
          ? `✓ EARNED  ·  TIER ${Math.min(3, stars || 1)}/3`
          : '✓ EARNED  ·  ACTIVE', {
          size: 11, color: '#7dce5a',
        });
      }

      if (isNew) {
        // Pill sits inside the reserved right column, clear of the title.
        g.fillStyle(GOLD, 1);
        g.fillRoundedRect(x + cardW - 62, y + 8, 46, 17, 3);
        label(x + cardW - 39, y + 11, 'NEW', { size: 10, color: '#2a1c08', bold: true, origin: [0.5, 0], stroke: 'transparent' });
      }
    });

    // Scroll track — reflects whole rows only.
    if (scrollMax > 0) {
      const trackX = 1234;
      const trackY = viewTop + 2;
      const trackH = viewH - 4;
      g.fillStyle(0x131f27, 1);
      g.fillRoundedRect(trackX, trackY, 7, trackH, 3.5);
      const thumbH = Math.max(30, trackH * (viewH / (viewH + scrollMax)));
      const thumbY = trackY + (trackH - thumbH) * (this.awardsScrollY / scrollMax);
      g.fillStyle(0x3d7f9c, 1);
      g.fillRoundedRect(trackX, thumbY, 7, thumbH, 3.5);
      g.fillStyle(ACCENT, 0.9);
      g.fillRoundedRect(trackX, thumbY, 7, Math.min(12, thumbH), 3.5);
    }

    // Footer legend with swatches — placed clear of the card grid.
    const footY = viewBottom + 18;
    g.fillStyle(0x0b151c, 1);
    g.fillRoundedRect(32, footY, 1216, 34, 7);
    g.lineStyle(1, 0x2a3e4a, 1);
    g.strokeRoundedRect(32, footY, 1216, 34, 7);
    const legend: Array<[number, string]> = [
      [GOLD, 'NEW'],
      [GOOD, 'EARNED'],
      [0x4a6a7c, 'LOCKED'],
    ];
    let lx = 48;
    legend.forEach(([colour, text]) => {
      g.fillStyle(colour, 1);
      g.fillRoundedRect(lx, footY + 13, 10, 10, 2);
      label(lx + 16, footY + 11, text, { size: 11, color: '#a8c0d0' });
      lx += 16 + text.length * 8 + 22;
    });
    label(lx, footY + 11, '★ shown only on incomplete skill awards', { size: 10, color: '#5f7484' });

    const s = saveManager.data.stats;
    label(1226, footY + 44, `kills ${s.kills}  ·  bosses ${s.bossesDefeated}  ·  max wave ${s.maxWave}  ·  abilities ${s.abilitiesUsed}`, {
      size: 10, color: '#5a6e7c', origin: [1, 0],
    });
  }

  // ---------- MISSION ----------

  private renderMission(g: Phaser.GameObjects.Graphics): void {
    const completed = saveManager.data.missionsComplete;
    const sel = getMission(this.selectedMission);
    const selMap = getMissionMap(sel.id);
    const selWaves = getMissionWaves(sel.id);
    const unlocked = isMissionUnlocked(sel.id, completed, saveManager.data.settings.devMode);
    const cleared = completed.includes(sel.id);
    const nextUnbeatenId = getNextUnbeatenMissionId(completed, saveManager.data.settings.devMode);
    const nextUnbeaten = nextUnbeatenId ? getMission(nextUnbeatenId) : undefined;
    const accentNum = Number(sel.accent.replace('#', '0x')) || 0x5ec8ff;
    const listX = 32;
    const listW = 300;
    const detailX = 350;
    const detailY = 88;
    const detailW = 898;
    const detailH = 596;
    const contentX = 378;
    const contentW = 468;
    const railX = 876;
    const railW = 344;

    this.add.text(listX, 66, 'OPERATIONS BOARD', {
      fontFamily: 'Consolas, monospace',
      fontSize: '13px',
      color: '#4a5560',
    });
    this.add.text(listX + listW, 68, nextUnbeaten ? 'NEXT UNBEATEN' : 'CAMPAIGN CLEAR', {
      fontFamily: 'Consolas, monospace',
      fontSize: '9px',
      color: nextUnbeaten ? '#d4a24a' : '#7dce5a',
    }).setOrigin(1, 0);

    // ---- Mission list ----
    g.fillStyle(0x0e141a, 1);
    g.fillRoundedRect(listX, detailY, listW, detailH, 8);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.strokeRoundedRect(listX, detailY, listW, detailH, 8);

    const missionRowH = MISSIONS.length > 12 ? 40 : 46;
    const missionCardH = missionRowH - 5;
    MISSIONS.forEach((m, i) => {
      const y = detailY + 8 + i * missionRowH;
      const open = isMissionUnlocked(m.id, completed, saveManager.data.settings.devMode);
      const done = completed.includes(m.id);
      const active = this.selectedMission === m.id;
      const next = nextUnbeaten?.id === m.id;

      if (active) {
        g.fillStyle(0x152028, 1);
        g.fillRoundedRect(listX + 8, y, listW - 16, missionCardH, 5);
        g.fillStyle(accentNum, 1);
        g.fillRect(listX + 8, y + 4, 3, missionCardH - 8);
      } else if (next) {
        g.fillStyle(0x2a2920, 1);
        g.fillRoundedRect(listX + 8, y, listW - 16, missionCardH, 5);
        g.fillStyle(0xd4a24a, 1);
        g.fillRect(listX + 8, y + 4, 3, missionCardH - 8);
      } else if (open) {
        g.fillStyle(0x10161c, 0.6);
        g.fillRoundedRect(listX + 8, y, listW - 16, missionCardH, 5);
      }

      const btn = this.add
        .rectangle(listX + listW / 2, y + missionCardH / 2, listW - 16, missionCardH, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      btn.setFillStyle(0x38d6f8, 0.001);
      btn.on('pointerdown', () => {
        if (!open) {
          audio.deny();
          const req = getMission(m.requires ?? 'kappa');
          this.flash = `LOCKED — clear ${req.name} first`;
          this.render();
          return;
        }
        this.selectedMission = m.id;
        audio.click();
        this.render();
      });
      btn.on('pointerover', () => {
        if (open) btn.setFillStyle(0x38d6f8, active ? 0.05 : 0.1);
      });
      btn.on('pointerout', () => btn.setFillStyle(0x38d6f8, 0.001));

      this.add.text(listX + 22, y + (missionRowH > 40 ? 8 : 6), String(m.index).padStart(2, '0'), {
        fontFamily: 'Consolas, monospace',
        fontSize: missionRowH > 40 ? '11px' : '10px',
        color: open ? m.accent : '#2a333c',
      });
      this.add.text(listX + 58, y + (missionRowH > 40 ? 6 : 5), m.name, {
        fontFamily: 'Consolas, monospace',
        fontSize: missionRowH > 40 ? '12px' : '11px',
        color: !open ? '#3a4550' : active ? '#e0e8f0' : next ? '#ffe0a6' : done ? '#7dce5a' : '#c8d4e0',
      });
      const note = open
        ? m.unlocksNote.length > 34
          ? m.unlocksNote.slice(0, 32) + '…'
          : m.unlocksNote
        : `Requires ${getMission(m.requires ?? 'kappa').name}`;
      this.add.text(listX + 58, y + (missionRowH > 40 ? 21 : 19), note.length > 27 ? note.slice(0, 25) + '…' : note, {
        fontFamily: 'Consolas, monospace',
        fontSize: missionRowH > 40 ? '10px' : '9px',
        color: open ? '#6a7a88' : '#3a4550',
      });

      const badge = done ? 'CLEARED' : next ? 'NEXT' : open ? 'READY' : 'LOCKED';
      this.add.text(listX + listW - 16, y + (missionRowH > 40 ? 9 : 8), badge, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: done ? '#7dce5a' : next ? '#d4a24a' : open ? '#5ec8ff' : '#3a4550',
      }).setOrigin(1, 0);
    });

    // ---- Detail panel ----
    g.fillStyle(0x0e141a, 1);
    g.fillRoundedRect(detailX, detailY, detailW, detailH, 8);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.strokeRoundedRect(detailX, detailY, detailW, detailH, 8);
    // accent stripe
    g.fillStyle(accentNum, unlocked ? 0.9 : 0.25);
    g.fillRect(detailX, detailY, 4, detailH);

    this.add.text(contentX, 108, `MISSION ${String(sel.index).padStart(2, '0')}`, {
      fontFamily: 'Consolas, monospace',
      fontSize: '12px',
      color: '#4a5560',
    });
    this.add.text(contentX, 130, sel.name, {
      fontFamily: 'Consolas, monospace',
      fontSize: '25px',
      color: unlocked ? sel.accent : '#4a5560',
    });

    // Status chips
    const status = cleared ? 'CLEARED' : unlocked ? 'ACTIVE' : 'LOCKED';
    const best = saveManager.data.bestScores[sel.id] ?? 0;
    const chips = [
      status,
      `${selWaves.length} WAVES`,
      `${selMap.pads.length} PADS`,
      `${selMap.command.hp * BASE_INTEGRITY_MULTIPLIER} INTEGRITY`,
      best ? `BEST ${best}` : 'NO SCORE',
    ];
    let chipX = contentX;
    chips.forEach((c, i) => {
      const w = c.length * 7 + 16;
      const col = i === 0
        ? cleared
          ? '#7dce5a'
          : unlocked
            ? '#5ec8ff'
            : '#4a5560'
        : '#6a7a88';
      g.fillStyle(0x152028, 1);
      g.fillRoundedRect(chipX, 164, w, 25, 4);
      g.lineStyle(1, 0x2a3e4a, 1);
      g.strokeRoundedRect(chipX, 164, w, 25, 4);
      this.add.text(chipX + 8, 169, c, {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: col,
      });
      chipX += w + 8;
    });

    this.add.text(contentX, 210, selMap.briefing, {
      fontFamily: 'Consolas, monospace',
      fontSize: '14px',
      color: '#9ad4e8',
      lineSpacing: 5,
      wordWrap: { width: contentW },
    });

    const devEnabled = saveManager.data.settings.devMode;

    // A dedicated right rail makes the map shot and its related actions read
    // as one workflow, without competing with mission briefing copy.
    this.renderMissionMapPreview(g, selMap, railX, 158, railW, 194, unlocked, accentNum);
    this.renderWaveIcons(g, selWaves, contentX, 302, contentW, 94);

    if (devEnabled) {
    // Keep one clear entry point; the tuning and capture controls live inside
    // Map Forge so the operations board stays focused on mission selection.
    const forge = this.add.rectangle(railX + railW / 2, 382, railW, 44, 0x152028, 1)
      .setStrokeStyle(1, 0x5ec8ff)
      .setInteractive({ useHandCursor: true });
    addRetroButtonHover(forge);
    forge.on('pointerdown', () => navigateTo(this, 'MapForge', { missionId: sel.id }));
    forge.on('pointerover', () => { if (devEnabled) forge.setFillStyle(0x1d3a49); });
    forge.on('pointerout', () => forge.setFillStyle(0x152028));
    this.add.text(railX + railW / 2, 374, 'OPEN MAP FORGE', {
      fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#aeeaff',
    }).setOrigin(0.5);
    }

    const userMapCount = listUserMaps().length;
    const userMapsY = devEnabled ? 440 : 382;
    const userMaps = this.add.rectangle(railX + railW / 2, userMapsY, railW, 44, 0x152028, 1)
      .setStrokeStyle(1, 0x75ddff)
      .setInteractive({ useHandCursor: true });
    userMaps.on('pointerover', () => userMaps.setFillStyle(0x1d3a49));
    userMaps.on('pointerout', () => userMaps.setFillStyle(0x152028));
    userMaps.on('pointerdown', () => { audio.click(); this.openUserMapsModal(sel.id); });
    this.add.text(railX + railW / 2, userMapsY - 8, `USER MAPS  ·  ${userMapCount} SAVED`, {
      fontFamily: 'Consolas, monospace', fontSize: '13px', color: '#aeeaff',
    }).setOrigin(0.5);
    this.add.text(railX + railW / 2, userMapsY + 11, 'OPEN LIBRARY  /  PLAY TEST', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#6a9cad',
    }).setOrigin(0.5);

    // Difficulty
    this.add.text(contentX, 418, 'DIFFICULTY', {
      fontFamily: 'Consolas, monospace',
      fontSize: '12px',
      color: '#4a5560',
    });
    const diffs: { id: 'normal' | 'hard' | 'nightmare'; label: string; sub: string }[] = [
      { id: 'normal', label: 'NORMAL', sub: '+40% hostile HP' },
      { id: 'hard', label: 'HARD', sub: '+150% HP · +8% speed' },
      { id: 'nightmare', label: 'NIGHTMARE', sub: '+300% HP · +15% speed' },
    ];
    diffs.forEach((d, i) => {
      const x = contentX + i * 160;
      const active = this.difficulty === d.id;
      const btn = this.add
        .rectangle(x, 440, 148, 54, active ? 0x1a2830 : 0x121a20, 1)
        .setStrokeStyle(1, active ? accentNum : 0x2a3e4a)
        .setOrigin(0, 0)
        .setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.difficulty = d.id;
        saveManager.data.difficulty = d.id;
        saveManager.save();
        audio.click();
        this.render();
      });
      this.add.text(x + 12, 449, d.label, {
        fontFamily: 'Consolas, monospace',
        fontSize: '13px',
        color: active ? '#e0e8f0' : '#6a7a88',
      });
      this.add.text(x + 12, 470, d.sub, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: active ? '#9ad4e8' : '#4a5560',
      });
    });

    // Research loadout
    const bonuses = researchManager.bonuses();
    const owned = researchManager.purchased;
    this.add.text(contentX, 518, 'RESEARCH LOADOUT', {
      fontFamily: 'Consolas, monospace',
      fontSize: '12px',
      color: '#4a5560',
    });
    const loadoutHeadingHit = this.add.rectangle(contentX + 68, 523, 146, 24, 0x000000, 0.001).setInteractive();
    loadoutHeadingHit.on('pointerover', () => this.showLoadoutTooltip(contentX, 548));
    loadoutHeadingHit.on('pointerout', () => this.hideLoadoutTooltip());
    if (!owned.length) {
      this.add.text(contentX, 542, 'No research purchased. Clear missions to earn DATA, then spend it in the RESEARCH tab.', {
        fontFamily: 'Consolas, monospace',
        fontSize: '12px',
        color: '#6a7a88',
        wordWrap: { width: contentW },
      });
    } else {
      const stats = [
        `GLOBAL DMG ×${WEAPON_DAMAGE_MULTIPLIER.toFixed(2)}`,
        `KESTREL ×${bonuses.damageMul.ballistic.toFixed(2)}`,
        `PYRE ×${bonuses.damageMul.thermal.toFixed(2)}`,
        `MORDANT ×${bonuses.damageMul.artillery.toFixed(2)}`,
        `HELIOS ×${bonuses.damageMul.energy.toFixed(2)}`,
        `SUPPLY +${bonuses.startSupplyBonus}`,
        `INTEGRITY +${bonuses.integrityBonus}`,
      ];
      let rx = contentX;
      let ry = 542;
      stats.forEach((s) => {
        const w = s.length * 6.2 + 14;
        if (rx > contentX && rx + w > contentX + contentW) {
          rx = contentX;
          ry += 28;
        }
        const chipX = rx;
        const chipY = ry;
        g.fillStyle(0x152028, 1);
        g.fillRoundedRect(chipX, chipY, w, 22, 3);
        this.add.text(chipX + 7, chipY + 4, s, {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: s.startsWith('GLOBAL') ? '#d4a24a' : '#9ad4e8',
        });
        const hit = this.add.rectangle(chipX + w / 2, chipY + 11, w, 28, 0x000000, 0.001).setInteractive();
        const tip = s.startsWith('GLOBAL')
          ? `GLOBAL WEAPON DAMAGE\nEvery tower, projectile and superweapon hit is multiplied by ${WEAPON_DAMAGE_MULTIPLIER.toFixed(2)} in the current balance pass. Research bonuses stack on top.`
          : undefined;
        hit.on('pointerover', () => this.showLoadoutTooltip(chipX, chipY + 32, tip));
        hit.on('pointerout', () => this.hideLoadoutTooltip());
        rx += w + 6;
      });
      this.add.text(contentX, ry + 30, `${owned.length} research nodes active`, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: '#4a5560',
      });
    }

    // Deploy CTA
    const dep = this.add
      .rectangle(contentX + contentW / 2, 632, contentW, 54, unlocked ? 0x152028 : 0x10161c, 1)
      .setStrokeStyle(2, unlocked ? accentNum : 0x2a3e4a)
      .setInteractive({ useHandCursor: unlocked });
    dep.on('pointerover', () => dep.setFillStyle(0x1a2830));
    dep.on('pointerout', () => dep.setFillStyle(unlocked ? 0x152028 : 0x10161c));
    dep.on('pointerdown', () => this.deploy());
    this.add
      .text(contentX + contentW / 2, 623, unlocked ? 'DEPLOY' : 'LOCKED', {
        fontFamily: 'Consolas, monospace',
        fontSize: '16px',
        color: unlocked ? '#e0e8f0' : '#4a5560',
      })
      .setOrigin(0.5);
    this.add
      .text(contentX + contentW / 2, 646, unlocked ? `${sel.name}  ·  [ENTER]` : 'Clear previous mission first', {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: unlocked ? sel.accent : '#3a4550',
      })
      .setOrigin(0.5);

    // Survival side CTA
    if (cleared) {
      const survBest = saveManager.data.survivalBest[sel.id];
      const survivalY = devEnabled ? 576 : 452;
      const surv = this.add
        .rectangle(railX + railW / 2, survivalY, railW, 44, 0x121a20, 1)
        .setStrokeStyle(1, COLORS.warn)
        .setInteractive({ useHandCursor: true });
      surv.on('pointerdown', () => {
        navigateTo(this, 'Battle', {
          difficulty: this.difficulty,
          survival: true,
          missionId: sel.id,
        });
      });
      this.add
        .text(
          railX + railW / 2,
          survivalY,
          survBest
            ? `SURVIVAL  ·  best wave ${survBest.waves}  ·  ${survBest.score} pts`
            : `SURVIVAL  ·  ${sel.name}`,
          {
            fontFamily: 'Consolas, monospace',
            fontSize: '12px',
            color: '#d4a24a',
          },
        )
        .setOrigin(0.5);
    } else {
      this.add
        .text(railX + railW / 2, 576, 'SURVIVAL UNLOCKS AFTER FIRST CLEAR', {
          fontFamily: 'Consolas, monospace',
          fontSize: '10px',
          color: '#3a4550',
        })
        .setOrigin(0.5);
    }
  }

  private closeUserMapsModal(): void {
    this.userMapsOverlay?.remove();
    this.userMapsOverlay = undefined;
  }

  private openUserMapsModal(defaultMissionId: string): void {
    this.closeUserMapsModal();
    let maps = listUserMaps();
    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(3,8,12,.84);font-family:Consolas,monospace;color:#c8d4e0;';
    const panel = document.createElement('div');
    panel.style.cssText = 'width:min(680px,calc(100vw - 32px));max-height:min(78vh,620px);display:flex;flex-direction:column;background:#0d1820;border:1px solid #5ec8ff;box-shadow:0 0 36px rgba(0,0,0,.6);padding:22px;';
    const title = document.createElement('div');
    title.textContent = 'USER MAPS';
    title.style.cssText = 'font-size:18px;color:#aeeaff;letter-spacing:2px;margin-bottom:4px;';
    const sub = document.createElement('div');
    sub.textContent = maps.length ? 'SELECT A LOCAL MAP TO PLAY OR EDIT' : 'NO LOCAL MAPS YET — CREATE ONE FROM SCRATCH';
    sub.style.cssText = 'font-size:11px;color:#6a9cad;margin-bottom:16px;';
    const list = document.createElement('div');
    list.style.cssText = 'overflow-y:auto;display:flex;flex-direction:column;gap:8px;padding-right:6px;';
    let selectedId = maps[0]?.id ?? '';
    const renderRows = () => {
      list.replaceChildren();
      maps.forEach((map) => {
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;align-items:center;gap:12px;padding:12px;border:1px solid ' + (map.id === selectedId ? '#d4a24a' : '#294452') + ';background:' + (map.id === selectedId ? '#172832' : '#101c24') + ';cursor:pointer;';
        row.onclick = () => { selectedId = map.id; renderRows(); };
        const copy = document.createElement('div');
        copy.style.cssText = 'flex:1;min-width:0;';
        const reviewSummary = map.ratings?.count
          ? ' · ★ ' + (map.ratings.sum / map.ratings.count).toFixed(1) + ' (' + map.ratings.count + ')'
          : ' · NO REVIEWS';
        const origin = 'PRIVATE · LOCAL' + (map.author ? ' · BY ' + this.escapeHtml(map.author) : '');
        copy.innerHTML = '<div style="font-size:13px;color:#dcefff;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">' + this.escapeHtml(map.name) + '</div><div style="font-size:10px;color:#6a9cad;margin-top:4px;">' + origin + reviewSummary + ' · UPDATED ' + new Date(map.updatedAt).toLocaleString() + ' · ' + map.layout.routes.length + ' LANES</div>';
        const play = document.createElement('button');
        play.textContent = 'PLAY';
        play.style.cssText = 'border:1px solid #5ec8ff;background:#152b36;color:#aeeaff;padding:8px 13px;font:11px Consolas,monospace;cursor:pointer;';
        play.onclick = (event) => { event.stopPropagation(); audio.click(); this.closeUserMapsModal(); navigateTo(this, 'Battle', { missionId: map.id, customMap: userMapToMapDef(map), customBackgroundDataUrl: map.backgroundDataUrl }); };
        const edit = document.createElement('button');
        edit.textContent = 'EDIT';
        edit.style.cssText = 'border:1px solid #52636d;background:#121d23;color:#9aaeb8;padding:8px 11px;font:11px Consolas,monospace;cursor:pointer;';
        edit.onclick = (event) => { event.stopPropagation(); audio.click(); this.closeUserMapsModal(); navigateTo(this, 'MapForge', { missionId: defaultMissionId, userMapId: map.id, openLibrary: true }); };
        const review = document.createElement('button');
        review.textContent = 'REVIEW';
        review.style.cssText = 'border:1px solid #705b2b;background:#231d12;color:#ffd27a;padding:8px 10px;font:11px Consolas,monospace;cursor:pointer;';
        review.onclick = (event) => { event.stopPropagation(); audio.click(); this.openMapReviewModal(map); };
        const remove = document.createElement('button');
        remove.textContent = '🗑';
        remove.title = 'Delete local map';
        remove.style.cssText = 'border:1px solid #6e3038;background:#241318;color:#ff8b8b;padding:8px 10px;font:13px Consolas,monospace;cursor:pointer;';
        remove.onclick = async (event) => {
          event.stopPropagation();
          if (!window.confirm('DELETE ' + map.name + '?')) return;
          audio.click();
          if (map.visibility === 'public') await mapService.unpublish(map.id);
          deleteUserMap(map.id);
          maps = maps.filter((candidate) => candidate.id !== map.id);
          selectedId = maps[0]?.id ?? '';
          renderRows();
          sub.textContent = maps.length ? 'SELECT A LOCAL MAP TO PLAY OR EDIT' : 'NO LOCAL MAPS YET — CREATE ONE FROM SCRATCH';
        };
        row.append(copy, play, edit, review, remove);
        list.append(row);
      });
    };
    renderRows();
    const footer = document.createElement('div');
    footer.style.cssText = 'display:flex;justify-content:space-between;gap:8px;margin-top:16px;';
    const newMap = document.createElement('button');
    newMap.textContent = 'NEW MAP FROM SCRATCH';
    newMap.style.cssText = 'border:1px solid #64d7ff;background:#1d4a5c;color:#d8f6ff;padding:9px 14px;font:11px Consolas,monospace;cursor:pointer;';
    newMap.onclick = () => { audio.click(); this.closeUserMapsModal(); navigateTo(this, 'MapForge', { missionId: defaultMissionId, openNewMap: true }); };
    const close = document.createElement('button');
    close.textContent = 'CLOSE';
    close.style.cssText = 'border:1px solid #31566a;background:#101a21;color:#9ad4e8;padding:9px 18px;font:11px Consolas,monospace;cursor:pointer;';
    close.onclick = () => this.closeUserMapsModal();
    footer.append(newMap, close);
    panel.append(title, sub, list, footer);
    overlay.append(panel);
    overlay.onclick = (event) => { if (event.target === overlay) this.closeUserMapsModal(); };
    document.body.append(overlay);
    this.userMapsOverlay = overlay;
  }

  private openMapReviewModal(map: ReturnType<typeof listUserMaps>[number]): void {
    const overlay = document.createElement('div'); overlay.style.cssText = 'position:fixed;inset:0;z-index:10001;display:flex;align-items:center;justify-content:center;background:rgba(3,8,12,.86);font-family:Consolas,monospace;color:#c8d4e0;';
    const panel = document.createElement('div'); panel.style.cssText = 'width:min(460px,calc(100vw - 32px));padding:22px;background:#0d1820;border:1px solid #d4a24a;box-shadow:0 0 36px rgba(0,0,0,.6);';
    const title = document.createElement('div'); title.textContent = 'REVIEW · ' + map.name; title.style.cssText = 'font-size:16px;color:#ffd27a;letter-spacing:1px;margin-bottom:6px;';
    const rating = document.createElement('select'); rating.innerHTML = '<option value="5">★★★★★ · 5</option><option value="4">★★★★☆ · 4</option><option value="3">★★★☆☆ · 3</option><option value="2">★★☆☆☆ · 2</option><option value="1">★☆☆☆☆ · 1</option>'; rating.style.cssText = 'width:100%;padding:9px;background:#071017;border:1px solid #335563;color:#ffd27a;font:12px Consolas,monospace;';
    const comment = document.createElement('textarea'); comment.placeholder = 'LEAVE A SHORT COMMENT'; comment.maxLength = 240; comment.style.cssText = 'box-sizing:border-box;width:100%;height:86px;margin-top:10px;padding:9px;background:#071017;border:1px solid #335563;color:#e5f8ff;font:11px Consolas,monospace;resize:vertical;';
    const info = document.createElement('div'); info.textContent = 'LOADING COMMUNITY NOTES…'; info.style.cssText = 'font-size:10px;color:#6a9cad;margin:12px 0;max-height:100px;overflow:auto;white-space:pre-wrap;';
    const actions = document.createElement('div'); actions.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;margin-top:12px;';
    const close = document.createElement('button'); close.textContent = 'CLOSE'; close.style.cssText = 'padding:8px 14px;background:#152028;border:1px solid #335563;color:#d8f6ff;font:10px Consolas,monospace;cursor:pointer;';
    const submit = document.createElement('button'); submit.textContent = 'POST REVIEW'; submit.style.cssText = 'padding:8px 14px;background:#1d4a5c;border:1px solid #64d7ff;color:#d8f6ff;font:10px Consolas,monospace;cursor:pointer;';
    close.onclick = () => overlay.remove(); submit.onclick = async () => {
      submit.disabled = true;
      const ok = await mapService.review(map.id, saveManager.data.commanderName || 'ARC-CADET', Number(rating.value), comment.value.trim());
      submit.textContent = ok ? 'SAVED' : 'PUBLISH MAP FIRST';
      submit.disabled = false;
    };
    actions.append(close, submit); panel.append(title, rating, comment, info, actions); overlay.append(panel); overlay.onclick = (event) => { if (event.target === overlay) overlay.remove(); }; document.body.append(overlay);
    void mapService.feedback(map.id).then((data) => {
      const average = data.ratings.count ? (data.ratings.sum / data.ratings.count).toFixed(1) : '—';
      const comments = data.comments.slice(0, 4).map((item) => item.author + ': ' + item.comment).join('\n') || 'NO COMMENTS YET — BE THE FIRST.';
      info.textContent = 'COMMUNITY RATING  ' + average + ' / 5  ·  ' + data.ratings.count + ' REVIEWS\n' + comments;
    });
  }

  private escapeHtml(value: string): string {
    return value.replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[char] ?? char);
  }

  private renderMissionMapPreview(
    g: Phaser.GameObjects.Graphics,
    map: MapDef,
    x: number,
    y: number,
    w: number,
    h: number,
    unlocked: boolean,
    accent: number,
  ): void {
    g.fillStyle(0x080d12, 1);
    g.fillRoundedRect(x, y, w, h, 6);
    g.lineStyle(1, unlocked ? accent : COLORS.panelEdge, unlocked ? 0.9 : 1);
    g.strokeRoundedRect(x, y, w, h, 6);

    const cleared = saveManager.data.missionsComplete.includes(map.id);
    const failed = saveManager.data.missionsFailed.includes(map.id);
    if (unlocked && !cleared) {
      const doorKey = failed ? 'unbeaten-door-graffiti' : 'unbeaten-door-clean';
      if (this.textures.exists(doorKey)) {
        this.add.image(x + w / 2, y + h / 2 + 4, doorKey).setDisplaySize(132, 132).setAlpha(0.94).setDepth(3);
      }
      this.add.text(x + w / 2, y + 10, failed ? 'ARCHIVE SEALED · TRY AGAIN' : 'ARCHIVE SEALED · UNBEATEN', {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: failed ? '#ff7474' : '#d4a24a', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(4);
      this.add.text(x + w / 2, y + h - 12, 'CLEAR THE MISSION TO REVEAL THE FIELD MAP', {
        fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#8fa8b8', align: 'center',
      }).setOrigin(0.5).setDepth(4);
      return;
    }

    const shotKey = unlocked ? this.captureMissionMapShot(map, accent) : '';
    if (shotKey && this.textures.exists(shotKey)) {
      this.add.image(x + w / 2, y + h / 2, shotKey).setDisplaySize(w - 2, h - 2);
      g.fillStyle(0x020609, 0.34);
      g.fillRoundedRect(x + 1, y + 1, w - 2, 22, 6);
      this.add.text(x + 12, y + 6, 'MISSION MAP SHOT', {
        fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#d9f7ff', letterSpacing: 0.7,
      });
      const authored = getMapLayout(map.id);
      this.add.text(x + 12, y + h - 17, `LIVE ROUTE  ·  ${authored.pads.length} VALID PADS`, {
        fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#e1f8ff',
      });
    } else {
      this.add.text(x + w / 2, y + h / 2 - 8, 'MAP CLASSIFIED', {
        fontFamily: 'Consolas, monospace', fontSize: '14px', color: '#6a7a88',
      }).setOrigin(0.5);
      this.add.text(x + w / 2, y + h / 2 + 14, 'CLEAR PRIOR MISSION TO REVEAL', {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#4a5560',
      }).setOrigin(0.5);
    }
  }

  /**
   * Build a real raster map shot from the current persisted layout. The old
   * implementation only recreated display objects over the same background,
   * which looked like a no-op and could leave a stale-looking thumbnail.
   */
  private captureMissionMapShot(map: MapDef, accent: number): string {
    if (typeof document === 'undefined') return '';
    const layout = getMapLayout(map.id);
    const signature = JSON.stringify({ routes: layout.routes, pads: layout.pads, entry: layout.entry, command: layout.command });
    const existing = this.previewShotKeyByMap.get(map.id);
    if (!this.previewShotForce && existing && this.previewShotSignatureByMap.get(map.id) === signature && this.textures.exists(existing)) {
      return existing;
    }

    const width = 688;
    const height = 388;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return '';

    ctx.fillStyle = '#071017';
    ctx.fillRect(0, 0, width, height);
    const bgKey = `battle-${map.id}-bg`;
    if (this.textures.exists(bgKey)) {
      const source = this.textures.get(bgKey).getSourceImage() as CanvasImageSource;
      ctx.globalAlpha = 0.74;
      ctx.drawImage(source, 0, 0, width, height);
      ctx.globalAlpha = 1;
    }
    ctx.fillStyle = 'rgba(1, 8, 13, 0.18)';
    ctx.fillRect(0, 0, width, height);

    const toX = (p: { x: number; y: number }) => ((p.x - MAP_FRAME.x) / MAP_FRAME.width) * width;
    const toY = (p: { x: number; y: number }) => ((p.y - MAP_FRAME.y) / MAP_FRAME.height) * height;
    const cssAccent = `#${accent.toString(16).padStart(6, '0')}`;
    const routes = layout.routes.length ? layout.routes : getMapRoutes(map);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    routes.forEach((route) => {
      if (!route.length) return;
      ctx.beginPath();
      route.forEach((p, index) => (index ? ctx.lineTo(toX(p), toY(p)) : ctx.moveTo(toX(p), toY(p))));
      ctx.strokeStyle = 'rgba(2, 10, 16, 0.92)';
      ctx.lineWidth = 15;
      ctx.stroke();
      ctx.beginPath();
      route.forEach((p, index) => (index ? ctx.lineTo(toX(p), toY(p)) : ctx.moveTo(toX(p), toY(p))));
      ctx.strokeStyle = cssAccent;
      ctx.globalAlpha = 0.92;
      ctx.lineWidth = 5;
      ctx.stroke();
      ctx.globalAlpha = 1;
    });
    layout.pads.forEach((pad) => {
      const px = toX(pad); const py = toY(pad);
      ctx.fillStyle = 'rgba(3, 16, 24, 0.92)';
      ctx.beginPath(); ctx.arc(px, py, 11, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#9beaff'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(px, py, 11, 0, Math.PI * 2); ctx.stroke();
    });
    const marker = (point: { x: number; y: number }, colour: string, radius: number) => {
      ctx.fillStyle = colour;
      ctx.beginPath(); ctx.arc(toX(point), toY(point), radius, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#ecfbff'; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(toX(point), toY(point), radius + 2, 0, Math.PI * 2); ctx.stroke();
    };
    marker(layout.entry, '#ff7a3c', 8);
    marker(layout.command, '#7dce5a', 9);

    const key = `mission-map-shot-${map.id}`;
    if (this.textures.exists(key)) this.textures.remove(key);
    this.textures.addCanvas(key, canvas);
    this.previewShotKeyByMap.set(map.id, key);
    this.previewShotSignatureByMap.set(map.id, signature);
    this.previewShotForce = false;
    return key;
  }

  private renderWaveIcons(
    g: Phaser.GameObjects.Graphics,
    waves: ReturnType<typeof getMissionWaves>,
    x: number,
    y: number,
    w: number,
    h: number,
  ): void {
    g.fillStyle(0x0a1014, 1);
    g.fillRoundedRect(x, y, w, h, 6);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.strokeRoundedRect(x, y, w, h, 6);
    this.add.text(x + 12, y + 8, 'WAVE SIGNATURE', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#4a5560',
    });
    const shown = waves.slice(0, 10);
    const cellW = (w - 24) / Math.max(1, shown.length);
    shown.forEach((wave, i) => {
      const kinds = [...new Set(wave.groups.map((group) => group.enemy))];
      const threat = kinds.some((kind) => kind === 'colossus' || kind === 'archon')
        ? 0xff5a5a
        : kinds.some((kind) => kind === 'goliath' || kind === 'plateback')
          ? 0xd4a24a
          : kinds.includes('dashling')
            ? 0xc8e85a
            : 0x6a7a88;
      const cx = x + 12 + cellW * i + cellW / 2;
      g.fillStyle(0x121c24, 1);
      g.fillRoundedRect(cx - cellW / 2 + 2, y + 26, cellW - 4, 40, 4);
      g.lineStyle(1, threat, 0.8);
      g.strokeRoundedRect(cx - cellW / 2 + 2, y + 26, cellW - 4, 40, 4);
      this.add.text(cx, y + 30, String(wave.id).padStart(2, '0'), {
        fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#c8d4e0',
      }).setOrigin(0.5, 0);
      const squadCount = wave.groups.reduce((sum, group) => sum + group.count, 0);
      this.add.text(cx + cellW / 2 - 7, y + 30, `×${squadCount}`, {
        fontFamily: 'Consolas, monospace', fontSize: '7px', color: '#d4a24a',
      }).setOrigin(1, 0);
      const primary = kinds[0];
      const primaryKey = WAVE_ENEMY_ART[primary]
        ?? PREMIUM_FAMILY_ART[ENEMIES[primary]?.family ?? 'mesh'];
      if (primaryKey && this.textures.exists(primaryKey)) {
        this.add.image(cx, y + 53, primaryKey).setDisplaySize(29, 29);
      } else {
        this.drawWaveBeastIcon(g, primary, cx, y + 53, threat);
      }
      if (kinds[1]) {
        const secondaryKey = WAVE_ENEMY_ART[kinds[1]]
          ?? PREMIUM_FAMILY_ART[ENEMIES[kinds[1]]?.family ?? 'mesh'];
        if (secondaryKey && this.textures.exists(secondaryKey)) this.add.image(cx + 12, y + 60, secondaryKey).setDisplaySize(16, 16);
      }
      const hit = this.add.rectangle(cx, y + 46, cellW - 4, 40, 0x000000, 0.001)
        .setInteractive({ useHandCursor: true });
      hit.on('pointerover', (pointer: Phaser.Input.Pointer) => {
        this.showWaveTooltip(primary, wave, pointer.x, pointer.y, threat);
      });
      hit.on('pointermove', (pointer: Phaser.Input.Pointer) => {
        if (this.waveTooltip) this.positionWaveTooltip(pointer.x, pointer.y);
      });
      hit.on('pointerout', () => this.hideWaveTooltip());
    });
    if (waves.length > shown.length) {
      this.add.text(x + w - 12, y + 8, `+${waves.length - shown.length}`, {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#6a7a88',
      }).setOrigin(1, 0);
    }
  }

  private showWaveTooltip(
    enemyId: string,
    wave: ReturnType<typeof getMissionWaves>[number],
    pointerX: number,
    pointerY: number,
    accent: number,
  ): void {
    this.hideWaveTooltip();
    const def = ENEMIES[enemyId];
    if (!def) return;
    const count = wave.groups.filter((group) => group.enemy === enemyId).reduce((sum, group) => sum + group.count, 0);
    const width = 268;
    const height = 86;
    const bg = this.add.graphics();
    bg.fillStyle(0x071017, 0.94);
    bg.fillRoundedRect(0, 0, width, height, 6);
    bg.lineStyle(1.2, accent, 0.95);
    bg.strokeRoundedRect(0, 0, width, height, 6);
    const title = this.add.text(10, 8, `WAVE ${String(wave.id).padStart(2, '0')}  ·  ${def.name.toUpperCase()}`, {
      fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#e0e8f0',
    });
    const body = this.add.text(10, 27, `${def.desc}\nHP ${def.hp}  ·  SPD ${def.speed}  ·  ARM ${def.armour}\nSIGNATURE COUNT ${count}${def.shield ? `  ·  SHIELD ${def.shield}` : ''}`, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8', lineSpacing: 3,
      wordWrap: { width: width - 20 },
    });
    this.waveTooltip = this.add.container(0, 0, [bg, title, body]).setDepth(90);
    this.positionWaveTooltip(pointerX, pointerY);
  }

  private positionWaveTooltip(pointerX: number, pointerY: number): void {
    if (!this.waveTooltip) return;
    const width = 268;
    const height = 86;
    this.waveTooltip.setPosition(
      Phaser.Math.Clamp(pointerX - width / 2, 8, GAME_WIDTH - width - 8),
      Phaser.Math.Clamp(pointerY + 16, UI_TOP_H + 8, GAME_HEIGHT - height - 8),
    );
  }

  private hideWaveTooltip(): void {
    this.waveTooltip?.destroy(true);
    this.waveTooltip = undefined;
  }

  private showLoadoutTooltip(x: number, y: number, override?: string): void {
    this.hideLoadoutTooltip();
    const width = 330;
    const height = override ? 94 : 112;
    const bg = this.add.graphics().setDepth(95);
    bg.fillStyle(0x071017, 0.96).fillRoundedRect(0, 0, width, height, 6);
    bg.lineStyle(1, COLORS.accent, 0.85).strokeRoundedRect(0, 0, width, height, 6);
    const text = this.add.text(10, 8,
      override ?? `RESEARCH LOADOUT\nActive research modifiers carried into the next sortie.\nGLOBAL DAMAGE = ×${WEAPON_DAMAGE_MULTIPLIER.toFixed(2)} before armour/resistance.\nSUPPLY = starting credits for towers, upgrades, barriers and bombs.\nINTEGRITY = command-core health; breaches reduce it to zero.`,
      { fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#c8d4e0', lineSpacing: 3, wordWrap: { width: width - 20 } },
    ).setDepth(96);
    this.loadoutTooltip = this.add.container(0, 0, [bg, text]).setDepth(95);
    this.loadoutTooltip.setPosition(Phaser.Math.Clamp(x, 12, GAME_WIDTH - width - 12), Phaser.Math.Clamp(y, 70, GAME_HEIGHT - height - 12));
  }

  private hideLoadoutTooltip(): void {
    this.loadoutTooltip?.destroy(true);
    this.loadoutTooltip = undefined;
  }

  private drawWaveBeastIcon(g: Phaser.GameObjects.Graphics, enemyId: string, cx: number, cy: number, colour: number): void {
    const family = ENEMIES[enemyId]?.family ?? 'mesh';
    g.lineStyle(1.4, colour, 0.95);
    g.fillStyle(colour, 0.78);
    if (family === 'robot') {
      g.fillRoundedRect(cx - 7, cy - 6, 14, 12, 2);
      g.strokeRoundedRect(cx - 7, cy - 6, 14, 12, 2);
      g.fillStyle(0x071017, 1);
      g.fillCircle(cx - 3, cy - 1, 1.5);
      g.fillCircle(cx + 3, cy - 1, 1.5);
      g.lineBetween(cx - 9, cy - 2, cx - 7, cy - 2);
      g.lineBetween(cx + 7, cy - 2, cx + 9, cy - 2);
    } else if (family === 'insect') {
      g.fillEllipse(cx, cy, 8, 12);
      g.strokeEllipse(cx, cy, 8, 12);
      for (const side of [-1, 1]) {
        g.lineBetween(cx + side * 3, cy - 3, cx + side * 10, cy - 8);
        g.lineBetween(cx + side * 4, cy + 1, cx + side * 11, cy + 2);
        g.lineBetween(cx + side * 3, cy + 5, cx + side * 9, cy + 9);
      }
    } else if (family === 'human') {
      g.fillCircle(cx, cy - 6, 4);
      g.fillRoundedRect(cx - 5, cy - 1, 10, 11, 2);
      g.strokeRoundedRect(cx - 5, cy - 1, 10, 11, 2);
      g.lineBetween(cx + 4, cy + 1, cx + 11, cy - 4);
    } else {
      // Unknown families still get a creature silhouette; never fall back to
      // the old circular enemy art, which reads as a generic resource orb.
      g.fillEllipse(cx, cy, 18, 14);
      g.strokeEllipse(cx, cy, 18, 14);
      g.fillStyle(0x071017, 1);
      g.fillCircle(cx - 3, cy - 2, 1.5);
      g.fillCircle(cx + 3, cy - 2, 1.5);
      g.lineStyle(1.2, colour, 0.95);
      g.lineBetween(cx - 5, cy - 6, cx - 8, cy - 11);
      g.lineBetween(cx + 5, cy - 6, cx + 8, cy - 11);
      g.lineBetween(cx - 5, cy + 5, cx - 10, cy + 10);
      g.lineBetween(cx + 5, cy + 5, cx + 10, cy + 10);
    }
  }

  private deploy(): void {
    audio.unlock();
    const sel = getMission(this.selectedMission);
    if (!isMissionUnlocked(sel.id, saveManager.data.missionsComplete, saveManager.data.settings.devMode)) {
      audio.deny();
      return;
    }
    navigateTo(this, 'Battle', {
      difficulty: this.difficulty,
      survival: false,
      missionId: sel.id,
    });
  }

  // ---------- RESEARCH ----------

  private researchRefundTotal(): number {
    return researchManager.purchased.reduce((sum, id) => {
      const node = RESEARCH.find((entry) => entry.id === id);
      return sum + (node?.cost ?? 0);
    }, 0);
  }

  private confirmResearchReset(): void {
    const refund = this.researchRefundTotal();
    if (!refund) {
      audio.deny();
      return;
    }
    saveManager.data.research = [];
    saveManager.adjustResearchPoints(refund);
    saveManager.save();
    this.resetResearchArmed = false;
    this.resetResearchTimer?.remove(false);
    this.resetResearchTimer = undefined;
    this.flash = `RESEARCH RESET — ${refund} DATA REFUNDED`;
    audio.upgrade();
    voice.play(this, 'upgrade_1', { cooldown: 2 });
    this.render();
  }

  private renderResearch(g: Phaser.GameObjects.Graphics): void {
    this.add.text(40, 78, 'ORION COMPACT LABS — RESEARCH', {
      fontFamily: 'Consolas, monospace',
      fontSize: '13px',
      color: '#6a7a88',
    });
    this.txtData = this.add.text(40, 98, `DATA POINTS  ${researchManager.points}`, {
      fontFamily: 'Consolas, monospace',
      fontSize: '16px',
      color: '#d4a24a',
    });
    this.add.text(240, 101, `GLOBAL WEAPON DAMAGE  ×${WEAPON_DAMAGE_MULTIPLIER.toFixed(2)}  ·  RESEARCH BONUSES STACK ON TOP`, {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#9ad4e8',
    });

    const resetW = 205;
    const resetX = GAME_WIDTH - 40 - resetW;
    const resetY = 94;
    const resetBg = this.add
      .rectangle(resetX, resetY, resetW, 28, 0x152028, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(1, 0x6a3e45)
      .setInteractive({ useHandCursor: true });
    const resetLabel = this.add.text(resetX + resetW / 2, resetY + 14, '', {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#ffaaa0',
    }).setOrigin(0.5);
    const paintReset = () => {
      const refund = this.researchRefundTotal();
      const armed = this.resetResearchArmed && refund > 0;
      resetLabel.setText(
        refund <= 0
          ? 'NO RESEARCH TO RESET'
          : armed
            ? `CONFIRM RESET  ·  +${refund} DATA`
            : `RESET RESEARCH  ·  REFUND ${refund}`,
      );
      resetLabel.setColor(refund <= 0 ? '#59656e' : armed ? '#ffe0a6' : '#ffaaa0');
      resetBg.setFillStyle(armed ? 0x4a2828 : 0x152028);
      resetBg.setStrokeStyle(1, refund <= 0 ? 0x2a3e4a : armed ? 0xffb347 : 0x6a3e45);
      if (resetBg.input) resetBg.input.enabled = refund > 0;
    };
    resetBg.on('pointerover', () => {
      if (this.researchRefundTotal() > 0) resetBg.setFillStyle(this.resetResearchArmed ? 0x61322c : 0x2a2028);
    });
    resetBg.on('pointerout', () => paintReset());
    resetBg.on('pointerdown', () => {
      if (this.researchRefundTotal() <= 0) {
        audio.deny();
        return;
      }
      if (this.resetResearchArmed) {
        this.confirmResearchReset();
        return;
      }
      this.resetResearchArmed = true;
      this.flash = 'CLICK RESET AGAIN TO REFUND ALL RESEARCH DATA';
      audio.click();
      paintReset();
      this.resetResearchTimer = this.time.delayedCall(4500, () => {
        this.resetResearchArmed = false;
        this.resetResearchTimer = undefined;
        this.flash = '';
        paintReset();
      });
    });
    paintReset();

    // ---- Category rail (tower art per branch) ----
    const railX = 40;
    const railY = 140;
    const railW = 172;
    const itemH = 31;
    RESEARCH_CATEGORIES.forEach((cat, i) => {
      const y = railY + i * itemH;
      const active = this.researchCategory === cat;
      const art = CATEGORY_ART[cat] ?? CATEGORY_ART.BALLISTICS;
      const catNodes = RESEARCH.filter((node) => node.category === cat);
      const maxData = catNodes.reduce((sum, node) => sum + node.cost, 0);
      const invested = catNodes
        .filter((node) => researchManager.isUnlocked(node.id))
        .reduce((sum, node) => sum + node.cost, 0);
      const progress = maxData > 0 ? invested / maxData : 0;
      const rect = this.add
        .rectangle(railX, y, railW, itemH - 4, active ? 0x1a2830 : 0x10161c, 1)
        .setOrigin(0, 0)
        .setStrokeStyle(1, active ? COLORS.accent : 0x24313c)
        .setInteractive({ useHandCursor: true });
      addRetroButtonHover(rect, active ? 0x1a2830 : 0x10161c);
      if (active) {
        g.fillStyle(art.colour, 1);
        g.fillRect(railX, y, 3, itemH - 4);
      }
      if (this.textures.exists(art.icon)) {
        this.add.image(railX + 16, y + (itemH - 4) / 2, art.icon).setDisplaySize(20, 20);
      }
      this.add.text(railX + 32, y + 7, cat, {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: active ? '#5ec8ff' : '#8aa0b0',
      });
      this.add.text(railX + railW - 8, y + 6, `${invested}/${maxData} DATA`, {
        fontFamily: 'Consolas, monospace',
        fontSize: '8px',
        color: invested >= maxData ? '#7dce5a' : active ? '#d4a24a' : '#6a7a88',
      }).setOrigin(1, 0);
      const barX = railX + 32;
      const barW = railW - 40;
      // Draw the progress on top of the card fill so the tiny bar remains visible.
      const progressG = this.add.graphics();
      progressG.fillStyle(0x1a242c, 1);
      progressG.fillRect(barX, y + 24, barW, 3);
      progressG.fillStyle(art.colour, active ? 0.95 : 0.55);
      progressG.fillRect(barX, y + 24, barW * progress, 3);
      rect.on('pointerover', () => !active && rect.setFillStyle(0x141c24));
      rect.on('pointerout', () => !active && rect.setFillStyle(0x10161c));
      rect.on('pointerdown', () => {
        this.researchCategory = cat;
        this.researchScrollX = 0;
        audio.click();
        this.render();
      });
    });

    // ---- Interconnected tree + detail panel ----
    this.researchScrollX = Phaser.Math.Clamp(this.researchScrollX, 0, this.researchScrollMax());
    this.tree = new ResearchTree(this, {
      x: 240,
      y: 126,
      w: 716,
      h: 440,
      category: this.researchCategory,
      scrollX: this.researchScrollX,
      onUnlock: (node) => {
        if (researchManager.unlock(node.id)) {
          audio.upgrade();
          this.flash = `RESEARCHED: ${node.name}`;
          if (node.tier >= 4) {
            voice.play(this, researchVoiceId(node.id), { priority: true, cooldown: 0 });
          } else if (!this.researchVoicePlayedThisRound) {
            this.researchVoicePlayedThisRound = true;
            voice.play(this, 'upgrade_1', { cooldown: 6 });
          }
          this.txtData?.setText(`DATA POINTS  ${researchManager.points}`);
          this.tree?.rebuild();
        } else {
          audio.deny();
        }
      },
      onExplain: (node) => {
        const family = node.category === 'ORBITAL' || node.category === 'ROLLING DEATH' ? 'SUPERWEAPON' : node.category === 'FORTIFICATION' ? 'BASE / SUPPORT' : `${node.category} PLATFORM`;
        this.flash = `${family}: ${node.name}  ·  ${node.desc}`;
        voice.playCategory(this, node.category === 'ORBITAL' ? 'ability' : node.category === 'FORTIFICATION' ? 'symbiosis' : 'upgrade', { cooldown: 12 });
      },
      onLocked: (node, reasons) => {
        audio.deny();
        const why = reasons.length ? reasons.join('  ·  ') : 'LOCKED';
        this.flash = `LOCKED: ${node.name}  —  ${why}`;
      },
    });

    // The fifth tier extends beyond the fixed research viewport. Keep the
    // graph readable at its authored card size and expose the extra columns
    // through a compact horizontal pan hint/track.
    const researchMax = this.researchScrollMax();
    this.researchScrollX = Phaser.Math.Clamp(this.researchScrollX, 0, researchMax);
    if (researchMax > 0) {
      const trackX = 240;
      const trackY = 569;
      const trackW = 716;
      const thumbW = Math.max(96, trackW * (trackW / (trackW + researchMax)));
      const thumbX = trackX + (trackW - thumbW) * (this.researchScrollX / researchMax);
      g.fillStyle(0x1a242c, 1);
      g.fillRoundedRect(trackX, trackY, trackW, 5, 2);
      g.fillStyle(COLORS.accent, 0.75);
      g.fillRoundedRect(thumbX, trackY, thumbW, 5, 2);
      this.add.text(trackX + trackW, trackY - 1, `PAN  ${Math.round((this.researchScrollX / researchMax) * 100)}%`, {
        fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88',
      }).setOrigin(1, 1);
      const pan = (direction: -1 | 1) => {
        this.researchScrollX = Phaser.Math.Clamp(this.researchScrollX + direction * 176, 0, researchMax);
        audio.click();
        this.render(false);
      };
      for (const [x, label, direction] of [[252, '‹', -1], [944, '›', 1]] as const) {
        const button = this.add.rectangle(x, trackY + 4, 24, 18, 0x152028, 1)
          .setStrokeStyle(1, COLORS.accent)
          .setInteractive({ useHandCursor: true });
        addRetroButtonHover(button);
        button.on('pointerdown', () => pan(direction));
        this.add.text(x, trackY + 4, label, {
          fontFamily: 'Consolas, monospace', fontSize: '14px', color: '#aeeaff',
        }).setOrigin(0.5);
      }
    }

    // A compact visual field guide keeps the practical meaning of evolution,
    // symbiosis and abilities visible while the player browses the research
    // graph. The same authored sprites used in battle make these systems easy
    // to recognise at a glance.
    const guideY = 580;
    const guideX = 240;
    const guideW = GAME_WIDTH - guideX - 40;
    g.fillStyle(0x0d141a, 0.96).fillRoundedRect(guideX, guideY, guideW, 84, 8);
    g.lineStyle(1, 0x31566a, 0.9).strokeRoundedRect(guideX, guideY, guideW, 84, 8);
    this.add.text(guideX + 14, guideY + 8, 'FIELD GUIDE  ·  WEAPONS + ABILITIES', {
      fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#5ec8ff',
    });
    const guideItems = [
      { key: 'tower-dronehive-v2', title: 'EVOLVE', body: 'Experimental chassis\nchange the primary weapon.' },
      { key: 'icon-tesla', title: 'SYMBIOSIS', body: 'Bolt-on pods add a\nsecondary weapon or aura.' },
      { key: 'rolling-death-balls-v1', title: 'ROLLING DEATH', body: 'Launches a lane-spanning\nsalvo from the Bastion.' },
      { key: 'ability-spear-v2', title: 'ORBITAL SPEAR', body: 'Click a zone for a\ndelayed area strike.' },
    ];
    const guideCardW = (guideW - 28) / guideItems.length;
    guideItems.forEach((item, i) => {
      const x = guideX + 10 + i * guideCardW;
      if (this.textures.exists(item.key)) this.add.image(x + 26, guideY + 50, item.key).setDisplaySize(38, 38);
      this.add.text(x + 52, guideY + 34, item.title, {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#d4a24a',
      });
      this.add.text(x + 52, guideY + 48, item.body, {
        fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#8aa0b0', lineSpacing: 2,
      });
    });

    this.add
      .text(40, GAME_HEIGHT - 40, 'Earn 15 DATA per map. Rank packets and research caches count inside that map budget.', {
        fontFamily: 'Consolas, monospace',
        fontSize: '11px',
        color: '#4a5560',
      });
  }

  // ---------- DATABASE ----------

  private renderDatabase(g: Phaser.GameObjects.Graphics): void {
    this.add.text(40, 90, 'DATABASE', {
      fontFamily: 'Consolas, monospace',
      fontSize: '14px',
      color: '#6a7a88',
    });
    const subtitle = this.dbFilter === 'towers'
      ? 'PLATFORM UPGRADE DOSSIERS  ·  WHEEL TO SCROLL'
      : this.dbFilter === 'superpowers'
        ? 'FIELD ABILITY DOSSIERS  ·  SELECT A CARD FOR QUICK READ'
        : 'HOSTILE CONTACT ARCHIVE  ·  WHEEL TO SCROLL';
    this.add.text(40, 112, subtitle, {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#4a5560',
    });

    // Filter
    const filters: { id: 'towers' | 'superpowers' | 'bestiary'; label: string }[] = [
      { id: 'towers', label: 'TOWERS' },
      { id: 'superpowers', label: 'SUPERPOWERS' },
      { id: 'bestiary', label: 'BESTIARY' },
    ];
    filters.forEach((f, i) => {
      const x = 380 + i * 130;
      const active = this.dbFilter === f.id;
      const btn = this.add
        .rectangle(x, 98, 110, 26, active ? 0x1a2830 : 0x152028, 1)
        .setStrokeStyle(1, active ? COLORS.accent : 0x2a3e4a)
        .setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.dbFilter = f.id;
        this.dbIndex = 0;
        this.dbScroll = 0;
        audio.click();
        this.render();
      });
      this.add
        .text(x, 98, f.label, {
          fontFamily: 'Consolas, monospace',
          fontSize: '11px',
          color: active ? '#5ec8ff' : '#6a7a88',
        })
        .setOrigin(0.5);
    });

    if (this.dbFilter === 'towers') this.renderTowerDb(g);
    else if (this.dbFilter === 'superpowers') this.renderSuperpowerDb(g);
    else this.renderEnemyDb(g);
  }

  private researchScrollMax(): number {
    const nodeTiers = new Set(RESEARCH
      .filter((node) => node.category === this.researchCategory)
      .map((node) => node.tier));
    const contentW = nodeTiers.size * 176 + Math.max(0, nodeTiers.size - 1) * 44;
    return Math.max(0, contentW - 716);
  }

  private databaseScrollMax(): number {
    const viewY = 140;
    const viewH = 520;
    if (this.dbFilter === 'towers') {
      return Math.max(0, this.towerDatabaseContentBottom() - (viewY + viewH - 10));
    }
    if (this.dbFilter === 'bestiary') {
      const rows = Math.ceil(Object.keys(ENEMIES).length / 2);
      return Math.max(0, 152 + rows * 38 - (viewY + viewH - 10));
    }
    return 0;
  }

  private towerDatabaseLayout(): { columns: number; cardW: number; cardH: number; gapX: number; gapY: number; xStart: number; mobile: boolean } {
    const browserWidth = typeof window !== 'undefined' ? window.innerWidth : GAME_WIDTH;
    const mobile = browserWidth <= 760;
    return {
      columns: mobile ? 1 : 2,
      cardW: mobile ? 1120 : 580,
      cardH: 450,
      gapX: 20,
      gapY: 18,
      xStart: 56,
      mobile,
    };
  }

  private towerDatabaseContentBottom(): number {
    const layout = this.towerDatabaseLayout();
    const towerRows = Math.ceil(TOWER_ORDER.length / layout.columns);
    const towerBottom = 156 + towerRows * layout.cardH + Math.max(0, towerRows - 1) * layout.gapY;
    const symRows = Math.ceil(SYMBIOSIS_ORDER.length / layout.columns);
    const symStart = towerBottom + 42;
    return symStart + 34 + symRows * 156 + Math.max(0, symRows - 1) * layout.gapY;
  }

  private renderSuperpowerDb(g: Phaser.GameObjects.Graphics): void {
    const cards = [
      ['Q', 'ORBITAL SPEAR', 'ability-spear-v2', '55s', 'Click a battlefield zone. Delayed impact with a large blast radius.', 'Fragments research can widen the strike radius.'],
      ['W', 'HARDLIGHT BARRIER', 'ability-barrier-v2', '25s', 'Anchor a temporary wall directly on a route. Enemies must break through it.', 'Emitter research reduces deployment cost and extends the hold.'],
      ['E', 'ROLLING DEATH', 'rolling-death-balls-v1', '75s', 'Launches six rolling bombs from the Bastion back up every hostile lane. Press T to switch EXPLOSIVE / SNOW after Cryo Conversion research.', 'Rolling Death research adds balls, unlocks five-second SNOW slows and reduces cooldown.'],
      ['R', 'EMP LANCE', 'ability-emp-v2', '90s', 'Stun every hostile currently on the field. Reserve it for breach windows.', 'Phase Arrestor and Blackout Field extend the stun duration.'],
    ] as const;
    g.fillStyle(0x0e141a, 1);
    g.fillRoundedRect(40, 140, 1200, 520, 8);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.strokeRoundedRect(40, 140, 1200, 520, 8);
    cards.forEach((card, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = 70 + col * 585;
      const y = 170 + row * 220;
      const tint = i === 0 ? 0xffb347 : i === 1 ? 0x66e0ff : i === 2 ? 0x7dce5a : 0xb06cff;
      g.fillStyle(0x152028, 0.95);
      g.fillRoundedRect(x, y, 545, 180, 8);
      g.lineStyle(1.5, tint, 0.8);
      g.strokeRoundedRect(x, y, 545, 180, 8);
      g.fillStyle(tint, 0.12);
      g.fillCircle(x + 62, y + 62, 38);
      g.lineStyle(2, tint, 0.9);
      g.strokeCircle(x + 62, y + 62, 28);
      if (this.textures.exists(card[2])) {
        this.add.image(x + 62, y + 62, card[2]).setDisplaySize(68, 68);
      } else {
        // Keep the dossier usable if an optional art pack is missing.
        this.add.text(x + 62, y + 52, card[0], { fontFamily: 'Consolas, monospace', fontSize: '20px', color: '#e0e8f0', fontStyle: 'bold' }).setOrigin(0.5);
      }
      this.add.text(x + 120, y + 24, card[1], { fontFamily: 'Consolas, monospace', fontSize: '16px', color: '#e0e8f0' });
      this.add.text(x + 120, y + 50, `COOLDOWN  ${card[3]}   ·   READY IN BATTLE HUD`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#d4a24a' });
      this.add.text(x + 24, y + 112, card[4], { fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#9ad4e8', wordWrap: { width: 490 }, lineSpacing: 3 });
      this.add.text(x + 24, y + 150, `RESEARCH LINK  ·  ${card[5]}`, { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#6a7a88', wordWrap: { width: 490 } });
    });
  }

  private renderEnemyDb(g: Phaser.GameObjects.Graphics): void {
    const unlocked = new Set(saveManager.data.unlockedEnemies);
    const ids = Object.keys(ENEMIES);
    const listX = 40;
    const listY = 140;
    const listW = 290;
    const listH = 520;
    this.dbScroll = Phaser.Math.Clamp(this.dbScroll, 0, this.databaseScrollMax());

    // The contact rail is its own masked scroll surface. This keeps the
    // selected dossier stable while still exposing the complete roster.
    g.fillStyle(0x0e141a, 1);
    g.fillRoundedRect(listX, listY, listW, listH, 8);
    g.lineStyle(1, COLORS.panelEdge, 1);
    g.strokeRoundedRect(listX, listY, listW, listH, 8);
    const listMask = this.make.graphics({ x: 0, y: 0 }, false);
    listMask.fillStyle(0xffffff, 1);
    listMask.fillRect(listX, listY, listW, listH);
    const listContent = this.add.container(0, -this.dbScroll).setMask(listMask.createGeometryMask());
    const listG = this.add.graphics();
    listContent.add(listG);
    ids.forEach((id, i) => {
      const col = i % 2;
      const row = Math.floor(i / 2);
      const x = 50 + col * 140;
      const y = 152 + row * 38;
      const known = unlocked.has(id);
      const active = this.dbIndex === i;
      const def = ENEMIES[id];
      if (active) {
        listG.fillStyle(0x1a2830, 1);
        listG.fillRoundedRect(x - 4, y - 2, 134, 34, 4);
        listG.fillStyle(def.colour, 1);
        listG.fillRect(x - 4, y - 2, 3, 34);
      }
      const btn = this.add
        .rectangle(x + 63, y + 15, 134, 34, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      btn.on('pointerdown', () => {
        this.dbIndex = i;
        audio.click();
        this.render();
      });
      listContent.add(btn);
      // Use the actual contact portrait at micro scale; the old coloured LED
      // dots were ambiguous and made the archive feel like a debug list.
      if (known) {
        const portraitKey = PREMIUM_ENEMY_ART[id] ?? PREMIUM_FAMILY_ART[def.family ?? 'mesh'];
        if (portraitKey && this.textures.exists(portraitKey)) {
          const portrait = this.add.image(x + 10, y + 15, portraitKey).setDisplaySize(25, 25);
          portrait.setAlpha(active ? 1 : 0.86);
          listContent.add(portrait);
        } else {
          listG.fillStyle(def.colour, 1).fillRoundedRect(x + 3, y + 8, 15, 15, 3);
        }
      } else {
        listG.lineStyle(1, 0x35434b, 0.9).strokeRoundedRect(x + 3, y + 8, 15, 15, 3);
      }
      const label = known ? def.name : '???';
      const listText = this.add.text(x + 22, y + 9, label.length > 15 ? label.slice(0, 14) + '…' : label, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: known ? (active ? '#5ec8ff' : '#c8d4e0') : '#3a4550',
      });
      listContent.add(listText);
      if (known && (def.boss || def.elite)) {
        const tag = this.add.text(x + 22, y + 21, def.boss ? 'BOSS' : 'ELITE', {
          fontFamily: 'Consolas, monospace',
          fontSize: '8px',
          color: def.boss ? '#ff5a5a' : '#ffb347',
        });
        listContent.add(tag);
      }
    });

    const listMax = this.databaseScrollMax();
    const trackY = listY + 12;
    const trackH = listH - 24;
    const thumbH = Math.max(44, trackH * (trackH / (trackH + listMax)));
    const thumbY = trackY + (trackH - thumbH) * (this.dbScroll / Math.max(1, listMax));
    g.fillStyle(0x101820, 1);
    g.fillRoundedRect(listX + listW - 12, trackY, 5, trackH, 2);
    g.fillStyle(COLORS.accent, 0.85);
    g.fillRoundedRect(listX + listW - 12, thumbY, 5, thumbH, 2);

    // Detail
    const id = ids[this.dbIndex];
    const e = ENEMIES[id];
    const known = unlocked.has(id);

    if (!known) {
      this.add.text(380, 200, 'CLASSIFIED — not yet encountered.\nDeploy and engage this contact to unlock intel.', {
        fontFamily: 'Consolas, monospace',
        fontSize: '14px',
        color: '#4a5560',
        lineSpacing: 6,
      });
      return;
    }

    // ---- Detail card ----
    const detailX = 350;
    const detailW = 890;
    g.fillStyle(0x0b1116, 0.96);
    g.fillRoundedRect(detailX, 140, detailW, 520, 8);
    g.lineStyle(1.5, e.boss ? 0xff2020 : e.elite ? 0xff8888 : e.colour, 0.85);
    g.strokeRoundedRect(detailX, 140, detailW, 520, 8);
    g.fillStyle(e.colour, 0.9);
    g.fillRect(detailX, 140, detailW, 3);

    // Model preview
    const cx = 470;
    const cy = 300;
    g.fillStyle(0x080d12, 1);
    g.fillCircle(cx, cy, 104);
    g.lineStyle(1, 0x24313c, 1);
    g.strokeCircle(cx, cy, 104);
    const spriteKey = PREMIUM_ENEMY_ART[id] ?? PREMIUM_FAMILY_ART[e.family ?? 'mesh'] ?? `enemy-${id}`;
    if (this.textures.exists(spriteKey)) {
      const img = this.add.image(cx, cy, spriteKey);
      img.setDisplaySize(188, 188);
    } else {
      this.drawBestiaryModel(g, e, cx, cy);
    }
    g.lineStyle(e.boss ? 3 : e.elite ? 2 : 1.5, e.boss ? 0xff2020 : e.elite ? 0xff8888 : e.colour, 0.9);
    g.strokeCircle(cx, cy, 110);
    this.add
      .text(cx, cy + 124, (e.family ?? 'mesh').toUpperCase(), {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: '#6a7a88',
      })
      .setOrigin(0.5);

    // Name + tags
    this.add.text(600, 160, e.name, {
      fontFamily: 'Consolas, monospace',
      fontSize: '22px',
      color: e.boss ? '#ff6a6a' : e.elite ? '#ffb347' : '#e0e8f0',
    });
    const tag = e.boss ? 'BOSS' : e.elite ? 'ELITE' : 'HOSTILE';
    this.add.text(600, 190, `${tag}  ·  ${(e.family ?? 'mesh').toUpperCase()}`, {
      fontFamily: 'Consolas, monospace',
      fontSize: '11px',
      color: e.boss ? '#ff5a5a' : e.elite ? '#ffb347' : '#7dce5a',
    });
    this.add.text(600, 212, e.desc, {
      fontFamily: 'Consolas, monospace',
      fontSize: '11px',
      color: '#9ad4e8',
      wordWrap: { width: 600 },
      lineSpacing: 3,
    });

    // Stat chips
    const stats: [string, string][] = [
      ['HP', `${e.hp}${e.shield ? ` +${e.shield}sh` : ''}`],
      ['ARMOUR', String(e.armour)],
      ['SPEED', String(e.speed)],
      ['BOUNTY', String(e.bounty)],
      ['BREACH', String(e.breachDamage)],
    ];
    stats.forEach(([label, value], i) => {
      const bx = 600 + i * 116;
      const by = 258;
      g.fillStyle(0x101820, 1);
      g.fillRoundedRect(bx, by, 106, 46, 5);
      g.lineStyle(1, 0x24313c, 1);
      g.strokeRoundedRect(bx, by, 106, 46, 5);
      this.add.text(bx + 8, by + 6, label, {
        fontFamily: 'Consolas, monospace',
        fontSize: '9px',
        color: '#6a7a88',
      });
      this.add.text(bx + 8, by + 20, value, {
        fontFamily: 'Consolas, monospace',
        fontSize: '15px',
        color: '#c8d4e0',
      });
    });

    // Damage-type response bars
    this.add.text(600, 322, 'DAMAGE RESPONSE', {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#4a5560',
    });
    const TYPES: DamageType[] = ['BALLISTIC', 'THERMAL', 'EXPLOSIVE', 'ENERGY'];
    TYPES.forEach((key, i) => {
      const by = 342 + i * 30;
      const mult = e.resist[key] ?? 1;
      this.add.text(600, by + 4, key, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: '#9ad4e8',
      });
      const trackX = 700;
      const trackW = 250;
      g.fillStyle(0x101820, 1);
      g.fillRect(trackX, by + 2, trackW, 12);
      const frac = Phaser.Math.Clamp((mult - 0.3) / 1.4, 0, 1);
      const col = mult > 1.1 ? COLORS.good : mult < 0.9 ? COLORS.danger : 0x4a5560;
      g.fillStyle(col, 0.85);
      g.fillRect(trackX, by + 2, trackW * frac, 12);
      g.fillStyle(0xffffff, 0.35);
      g.fillRect(trackX + trackW * ((1 - 0.3) / 1.4), by, 1, 16);
      const verdict = mult > 1.15 ? 'WEAK' : mult < 0.85 ? 'RESIST' : 'NEUTRAL';
      this.add.text(trackX + trackW + 12, by + 4, `×${mult.toFixed(2)}  ${verdict}`, {
        fontFamily: 'Consolas, monospace',
        fontSize: '10px',
        color: mult > 1.1 ? '#7dce5a' : mult < 0.9 ? '#ff6a6a' : '#6a7a88',
      });
    });

    // Traits
    const traits: string[] = [];
    if (e.shield) traits.push(`SHIELD ${e.shield} (+${e.shieldRegen ?? 0}/s)`);
    if (e.regen) traits.push(`REGEN ${e.regen}/s`);
    if (e.ranged) traits.push(`RANGED ${e.range ?? 140}px`);
    if (e.stunTowers) traits.push(`JAMS ${e.stunRadius ?? 70}px`);
    if (e.spawnChild) traits.push(`SPAWNS ${ENEMIES[e.spawnChild]?.name ?? e.spawnChild}`);
    if (e.phases?.length) traits.push(`PHASES ${e.phases.length}`);
    if (!traits.length) traits.push('NO SPECIAL TRAITS');
    this.add.text(600, 470, 'TRAITS', {
      fontFamily: 'Consolas, monospace',
      fontSize: '10px',
      color: '#4a5560',
    });
    this.add.text(600, 488, traits.map((t) => `· ${t}`).join('\n'), {
      fontFamily: 'Consolas, monospace',
      fontSize: '11px',
      color: '#c8d4e0',
      lineSpacing: 4,
    });
  }

  /** Static family silhouette used in the bestiary preview. */
  private drawBestiaryModel(
    g: Phaser.GameObjects.Graphics,
    e: { colour: number; family?: string; boss?: boolean },
    cx: number,
    cy: number,
  ): void {
    const r = 34;
    const col = e.colour;
    const fam = e.family ?? 'mesh';
    if (fam === 'insect') {
      g.lineStyle(2, 0x2a3a12, 1);
      for (let i = 0; i < 3; i++) {
        const along = (i - 1) * r * 0.5;
        for (const side of [-1, 1]) {
          g.lineBetween(cx + along, cy, cx + along + side * r * 0.7, cy - r * 0.55);
          g.lineBetween(cx + along + side * r * 0.7, cy - r * 0.55, cx + along + side * r * 1.1, cy + r * 0.15);
        }
      }
      g.fillStyle(0x6a7a2a, 1);
      g.fillEllipse(cx, cy + r * 0.75, r * 0.95, r * 0.6);
      g.fillStyle(col, 1);
      g.fillCircle(cx, cy, r * 0.62);
      g.fillStyle(0x1e2a10, 1);
      g.fillCircle(cx, cy - r * 0.68, r * 0.36);
    } else if (fam === 'robot') {
      g.fillStyle(0x141c24, 1);
      g.fillRoundedRect(cx - r * 0.98, cy - r * 0.5, r * 0.5, r * 1.25, 2);
      g.fillRoundedRect(cx + r * 0.48, cy - r * 0.5, r * 0.5, r * 1.25, 2);
      g.fillStyle(col, 1);
      g.fillRoundedRect(cx - r * 0.8, cy - r * 0.7, r * 1.6, r * 1.4, 4);
      g.lineStyle(2, 0x0e1a22, 1);
      g.strokeRoundedRect(cx - r * 0.8, cy - r * 0.7, r * 1.6, r * 1.4, 4);
      g.fillStyle(0x2a3844, 1);
      g.fillCircle(cx, cy - r * 0.1, r * 0.42);
      g.lineStyle(3, 0x0e1a22, 1);
      g.lineBetween(cx, cy - r * 0.1, cx + r * 1.35, cy - r * 0.1);
      g.fillStyle(0x66e0ff, 0.9);
      g.fillCircle(cx, cy + r * 0.42, r * 0.2);
    } else if (fam === 'human') {
      g.lineStyle(3, 0x2a2a2a, 1);
      g.lineBetween(cx - r * 0.25, cy + r * 0.3, cx - r * 0.45, cy + r * 1.05);
      g.lineBetween(cx + r * 0.25, cy + r * 0.3, cx + r * 0.45, cy + r * 1.05);
      g.fillStyle(col, 1);
      g.fillRoundedRect(cx - r * 0.45, cy - r * 0.5, r * 0.9, r * 1.0, 3);
      g.fillStyle(0xd8b890, 1);
      g.fillCircle(cx, cy - r * 0.78, r * 0.36);
      g.fillStyle(0x66e0ff, 0.9);
      g.fillCircle(cx, cy - r * 0.78, r * 0.15);
      g.lineStyle(3, 0x1a1a1a, 1);
      g.lineBetween(cx, cy - r * 0.2, cx + r * 1.15, cy - r * 0.2);
      g.fillStyle(0x66e0ff, 0.9);
      g.fillCircle(cx + r * 1.15, cy - r * 0.2, 3);
    } else {
      g.fillStyle(col, 1);
      g.fillCircle(cx, cy, r);
      g.lineStyle(3, 0x1a2e12, 1);
      g.strokeCircle(cx, cy, r);
      if (e.boss) {
        for (let i = 0; i < 6; i++) {
          const a = (i * Math.PI) / 3;
          g.lineStyle(3, col, 1);
          g.lineBetween(cx, cy, cx + Math.cos(a) * (r + 12), cy + Math.sin(a) * (r + 12));
        }
      }
    }
  }

  private renderTowerDb(_g: Phaser.GameObjects.Graphics): void {
    const viewX = 40;
    const viewY = 140;
    const viewW = 1200;
    const viewH = 520;
    const layout = this.towerDatabaseLayout();
    // Two cards keep the full dossier readable on desktop; narrow viewports
    // deliberately collapse to one card per row instead of squeezing columns.
    const { columns, cardW, cardH, gapX, gapY, xStart } = layout;
    const maxScroll = Math.max(0, this.towerDatabaseContentBottom() - (viewY + viewH - 10));
    this.dbScroll = Phaser.Math.Clamp(this.dbScroll, 0, maxScroll);

    // The manual is intentionally taller than the viewport. A geometry mask keeps
    // stage/evolution notes inside the dossier window while the scrollbar stays fixed.
    const maskShape = this.make.graphics({ x: 0, y: 0 }, false);
    maskShape.fillStyle(0xffffff, 1);
    maskShape.fillRect(viewX, viewY, viewW, viewH);
    const content = this.add.container(0, -this.dbScroll).setMask(maskShape.createGeometryMask());
    const cg = this.add.graphics();
    content.add(cg);

    const addText = (x: number, y: number, text: string, style: Phaser.Types.GameObjects.Text.TextStyle) => {
      const obj = this.add.text(x, y, text, style);
      content.add(obj);
      return obj;
    };
    const addImage = (x: number, y: number, key: string) => {
      const obj = this.add.image(x, y, key);
      content.add(obj);
      return obj;
    };
    const pct = (v: number) => `${Math.round(v * 100)}%`;
    const stageEffects = (s: (typeof TOWERS)[string]['stages'][number]): string => {
      const effects: string[] = [];
      if (s.splash) effects.push(`SPLASH ${s.splash}`);
      if (s.burnDps) effects.push(`BURN ${s.burnDps}/s`);
      if (s.slowPct) effects.push(`SLOW ${pct(s.slowPct)}`);
      if (s.armourPen) effects.push(`PEN ${pct(s.armourPen)}`);
      if (s.critChance) effects.push(`CRIT ${pct(s.critChance)}`);
      if (s.projectiles && s.projectiles > 1) effects.push(`SALVO ×${s.projectiles}`);
      if (s.homing) effects.push('HOMING');
      if (s.chain) effects.push(`CHAIN ${s.chain}`);
      return effects.length ? effects.join(' · ') : 'DIRECT FIRE';
    };

    cg.fillStyle(0x0e141a, 1);
    cg.fillRoundedRect(viewX, viewY, viewW, viewH, 8);
    cg.lineStyle(1, COLORS.panelEdge, 1);
    cg.strokeRoundedRect(viewX, viewY, viewW, viewH, 8);

    TOWER_ORDER.forEach((tid, i) => {
      const def = TOWERS[tid];
      const colIdx = i % columns;
      const rowIdx = Math.floor(i / columns);
      const x = xStart + colIdx * (cardW + gapX);
      const y = 156 + rowIdx * (cardH + gapY);
      const col = def.colour;
      cg.fillStyle(0x152028, 1);
      cg.fillRoundedRect(x, y, cardW, cardH, 6);
      cg.lineStyle(1.5, col, 0.8);
      cg.strokeRoundedRect(x, y, cardW, cardH, 6);
      cg.fillStyle(col, 0.08);
      cg.fillRect(x + 1, y + 1, cardW - 2, 4);

      const v3Key = `tower-${tid}-v3`;
      const key = this.textures.exists(v3Key)
        ? v3Key
        : tid === 'dronehive' && this.textures.exists('tower-dronehive-v2')
          ? 'tower-dronehive-v2'
          : `tower-${tid}`;
      if (this.textures.exists(key)) {
        const img = addImage(x + 46, y + 46, key);
        const s = Math.min(66 / img.width, 66 / img.height);
        img.setScale(s);
      } else {
        cg.fillStyle(col, 0.9);
        cg.fillCircle(x + 46, y + 46, 18);
      }

      addText(x + 84, y + 14, def.short, {
        fontFamily: 'Consolas, monospace', fontSize: '16px', color: '#c8d4e0',
      });
      addText(x + 84, y + 34, def.name, {
        fontFamily: 'Consolas, monospace', fontSize: '11px', color: '#6ec6ff',
      });
      addText(x + 12, y + 84, def.desc, {
        fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#8aa0b0',
        wordWrap: { width: cardW - 24 }, lineSpacing: 2,
      });
      const origins = TOWER_ORDER
        .filter((sourceId) => TOWERS[sourceId].evolvesTo?.some((evolution) => evolution.to === tid))
        .map((sourceId) => TOWERS[sourceId].short);
      addText(x + 12, y + 118, `ROLE  ${def.targeting.toUpperCase()}  ·  PRIMARY ${def.stages[0].damageType}${origins.length ? `  ·  EVOLVED FROM ${origins.join(' / ')}` : ''}`, {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8',
        wordWrap: { width: cardW - 24 },
      });

      def.stages.forEach((s, si) => {
        const sy = y + 136 + si * 40;
        addText(x + 12, sy, `${si + 1}. ${s.name}  $${s.cost}`, {
          fontFamily: 'Consolas, monospace', fontSize: '11px',
          color: si === 3 ? '#d4a24a' : '#c8d4e0',
        });
        addText(x + 12, sy + 13, `DMG ${s.damage}  RNG ${s.range}  ROF ${s.fireRate}/s`, {
          fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8',
        });
        addText(x + 12, sy + 25, stageEffects(s), {
          fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#6a7a88',
          wordWrap: { width: cardW - 24 },
        });
      });

      addText(x + 12, y + 300, 'UPGRADE PATH', {
        fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#4a5560',
      });
      const evolutions = def.evolvesTo ?? [];
      if (evolutions.length) {
        evolutions.forEach((evo, ei) => {
          const target = TOWERS[evo.to];
          addText(x + 12, y + 316 + ei * 20, `→ ${evo.name}  $${evo.cost}  —  ${evo.desc}`, {
            fontFamily: 'Consolas, monospace', fontSize: '9px',
            color: target ? `#${target.colour.toString(16).padStart(6, '0')}` : '#9ad4e8',
            wordWrap: { width: cardW - 24 }, lineSpacing: 1,
          });
        });
      } else {
        addText(x + 12, y + 316, '→ STANDARD PLATFORM · NO EVOLUTION REFIT', {
          fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#6a7a88',
        });
      }
      const moduleDefs = (def.symbiosis ?? []).map((id) => SYMBIOSIS[id]).filter(Boolean);
      addText(x + 12, y + 354, 'SYMBIOSIS MODULES', {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#4a5560',
      });
      if (!moduleDefs.length) {
        addText(x + 12, y + 368, 'NONE', { fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#6a7a88' });
      } else {
        moduleDefs.forEach((module, mi) => {
          const iconKey = module.id === 'pod_missile' ? 'symbiosis-pod-missile-v1'
            : module.id === 'pod_autocannon' ? 'symbiosis-pod-autocannon-v1'
              : module.id === 'pod_arc' ? 'symbiosis-pod-arc-v1'
                : module.id === 'pod_shield' ? 'symbiosis-pod-shield-v1'
                  : module.id === 'pod_laser' ? 'symbiosis-pod-laser-v1'
                    : module.id === 'pod_frost' ? 'symbiosis-pod-frost-v1'
                      : module.id === 'pod_plasma' ? 'symbiosis-pod-plasma-v1'
                        : module.id === 'pod_cluster' ? 'symbiosis-pod-cluster-v1' : '';
          if (mi === 0 && iconKey && this.textures.exists(iconKey)) addImage(x + cardW - 34, y + 360, iconKey).setDisplaySize(34, 34);
          addText(x + 12, y + 368 + mi * 13, `${module.short}  ·  ${module.desc}`, {
            fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#9ad4e8',
            wordWrap: { width: cardW - 24 }, lineSpacing: 1,
          });
        });
      }
    });

    // Give every bolt-on its own dossier as well as listing compatibility on
    // the host platform cards. This makes the symbiosis layer searchable from
    // the same database view instead of hiding it inside tower menus.
    const towerRows = Math.ceil(TOWER_ORDER.length / columns);
    const towerBottom = 156 + towerRows * cardH + Math.max(0, towerRows - 1) * gapY;
    const symSectionY = towerBottom + 42;
    const symCardH = 156;
    cg.fillStyle(0x101b23, 1);
    cg.fillRoundedRect(viewX, symSectionY, viewW, 34, 6);
    cg.lineStyle(1, 0x396644, 0.9);
    cg.strokeRoundedRect(viewX, symSectionY, viewW, 34, 6);
    addText(56, symSectionY + 8, 'SYMBIOSIS MODULE ARCHIVE', {
      fontFamily: 'Consolas, monospace', fontSize: '12px', color: '#7dce5a',
    });
    addText(390, symSectionY + 10, layout.mobile ? 'ONE CARD PER ROW · WHEEL TO SCROLL' : 'BOLT-ON DOSSIERS · TWO CARDS ABREAST', {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#6a7a88',
    });
    const symIconKeys: Record<string, string> = {
      pod_missile: 'symbiosis-pod-missile-v1',
      pod_salvo: 'symbiosis-pod-missile-v1',
      pod_autocannon: 'symbiosis-pod-autocannon-v1',
      pod_arc: 'symbiosis-pod-arc-v1',
      pod_laser: 'symbiosis-pod-laser-v1',
      pod_frost: 'symbiosis-pod-frost-v1',
      pod_plasma: 'symbiosis-pod-plasma-v1',
      pod_cluster: 'symbiosis-pod-cluster-v1',
      pod_cryo: 'symbiosis-pod-frost-v1',
      pod_uplink: 'symbiosis-addon-icons-v1',
      pod_shield: 'symbiosis-addon-icons-v1',
    };
    SYMBIOSIS_ORDER.forEach((moduleId, i) => {
      const module = SYMBIOSIS[moduleId];
      if (!module) return;
      const colIdx = i % columns;
      const rowIdx = Math.floor(i / columns);
      const x = xStart + colIdx * (cardW + gapX);
      const y = symSectionY + 42 + rowIdx * (symCardH + gapY);
      cg.fillStyle(0x152028, 1);
      cg.fillRoundedRect(x, y, cardW, symCardH, 6);
      cg.lineStyle(1.5, module.colour, 0.8);
      cg.strokeRoundedRect(x, y, cardW, symCardH, 6);
      cg.fillStyle(module.colour, 0.08);
      cg.fillRect(x + 1, y + 1, cardW - 2, 4);
      const iconKey = symIconKeys[moduleId];
      if (iconKey && this.textures.exists(iconKey)) addImage(x + 42, y + 44, iconKey).setDisplaySize(56, 56);
      addText(x + 82, y + 16, module.short, {
        fontFamily: 'Consolas, monospace', fontSize: '15px', color: '#c8d4e0',
      });
      addText(x + 82, y + 37, `${module.name}  ·  $${module.cost}`, {
        fontFamily: 'Consolas, monospace', fontSize: '10px', color: '#d4a24a',
      });
      addText(x + 82, y + 58, module.desc, {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#9ad4e8',
        wordWrap: { width: cardW - 96 }, lineSpacing: 2,
      });
      const effect = module.buffRate
        ? `SUPPORT  +${pct(module.buffRate)} RATE  ·  +${pct(module.buffRange ?? 0)} RANGE`
        : module.auraRate
          ? `AURA  +${pct(module.auraRate)} RATE  ·  ${module.auraRange}px`
          : `DMG ${module.damage}  RNG ${module.range}  ROF ${module.fireRate}/s`;
      addText(x + 12, y + 112, effect, {
        fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#c8d4e0',
      });
      const hosts = TOWER_ORDER
        .filter((towerId) => TOWERS[towerId].symbiosis?.includes(moduleId))
        .map((towerId) => TOWERS[towerId].short)
        .join(' / ');
      addText(x + 12, y + 132, `HOST PLATFORMS  ${hosts || '—'}`, {
        fontFamily: 'Consolas, monospace', fontSize: '8px', color: '#6a7a88',
        wordWrap: { width: cardW - 24 },
      });
    });

    // Fixed scrollbar and a live position cue make the interaction discoverable.
    const trackY = viewY + 10;
    const trackH = viewH - 20;
    const thumbH = Math.max(44, trackH * (trackH / (trackH + maxScroll)));
    const thumbY = trackY + (trackH - thumbH) * (this.dbScroll / Math.max(1, maxScroll));
    _g.fillStyle(0x101820, 1);
    _g.fillRoundedRect(1224, trackY, 6, trackH, 3);
    _g.fillStyle(COLORS.accent, 0.85);
    _g.fillRoundedRect(1224, thumbY, 6, thumbH, 3);
    this.add.text(1070, 672, `SCROLL  ${Math.round((this.dbScroll / Math.max(1, maxScroll)) * 100)}%`, {
      fontFamily: 'Consolas, monospace', fontSize: '9px', color: '#4a5560',
    });
  }
}
